'use strict'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import {
  makeStyles,
  Box,
  Button,
  Typography,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  Divider,
  Paper,
  TextField,
  Popover,
  Collapse,
  IconButton,
  Badge,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import PlaceIcon from '@material-ui/icons/Place'
import RoomIcon from '@material-ui/icons/Room'
import StarsIcon from '@material-ui/icons/Stars'
import NearMeIcon from '@material-ui/icons/NearMe'
import FilterListIcon from '@material-ui/icons/FilterList'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import SkipNextIcon from '@material-ui/icons/SkipNext'
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'
import LockIcon from '@material-ui/icons/Lock'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import { LoadScript, GoogleMap, Marker, Polyline } from '@react-google-maps/api'
import { getCrewColor, getRouteColor } from './crewColors'

const MAP_CONTAINER_STYLE = { width: '100%', height: 'calc(100vh - 260px)', minHeight: 520 }
const HMI_YARD = { lat: 33.43730763333812, lng: -112.11879940396678, label: 'HMI Yard' }
const DEFAULT_CENTER = { lat: 33.45, lng: -112.07 } // Phoenix, AZ
const DEFAULT_ZOOM = 10
const WEEKLY_OT_THRESHOLD_MIN = 2400
const DAILY_OT_THRESHOLD_MIN = 480
const SUGGESTION_PIN_COLORS = [
  '#E91E63', '#9C27B0', '#3F51B5', '#00BCD4', '#FF9800',
  '#8BC34A', '#795548', '#607D8B', '#F44336', '#009688'
]
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const WORK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']

const toMiles = (distanceKm) => distanceKm * 0.621371

const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const toRad = (value) => (value * Math.PI) / 180
  const earthRadiusKm = 6371
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return toMiles(earthRadiusKm * c)
}

const formatMinutes = (minutes) => {
  const num = Number(minutes || 0)
  if (!Number.isFinite(num)) return '0m'
  const rounded = Math.round(num)
  const h = Math.floor(rounded / 60)
  const m = rounded % 60
  if (h <= 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

const formatDeltaMinutes = (minutes) => {
  const value = Number(minutes || 0)
  const sign = value >= 0 ? '+' : '-'
  return `${sign}${formatMinutes(Math.abs(value))}`
}

const getFrequencyLabelFromCode = (frequencyCode) => {
  const code = String(frequencyCode || '').trim().toUpperCase()
  const map = {
    D: 'Daily',
    MWF: 'Every Other Day (M-W-F)',
    TW: 'Twice a Week',
    W: 'Weekly',
    BW: 'Bi-Weekly',
    BW_A: 'Bi-Weekly (Week A)',
    BW_B: 'Bi-Weekly (Week B)',
    M: 'Monthly',
    Q: 'Quarterly',
    SA: 'Semi-Annually',
    A: 'Annually',
    UNKNOWN: 'Unknown'
  }
  return map[code] || 'Unknown'
}

const parseYmd = (value) => {
  const input = String(value || '').trim()
  const date = input ? new Date(`${input}T00:00:00`) : new Date()
  if (Number.isNaN(date.getTime())) return new Date()
  return date
}

const toYmd = (date) => {
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

const addDays = (date, days) => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

const getMonday = (date) => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  return addDays(d, diff)
}

const getIsoWeek = (dateInput) => {
  const date = new Date(Date.UTC(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate()))
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7)
}

const getWeekTypeForDate = (date) => {
  const week = getIsoWeek(date)
  return week % 2 === 0 ? 'A' : 'B'
}

const getDayName = (date) => DAY_NAMES[new Date(date).getDay()] || null

const normalizeDaysForRow = (row = {}) => {
  const days = Array.isArray(row.daysOfWeek) ? row.daysOfWeek.filter(Boolean) : []
  if (days.length) return days
  if (Array.isArray(row.preferredDays) && row.preferredDays.length) {
    return row.preferredDays.filter(Boolean)
  }
  if (row.preferredDay) return [row.preferredDay]
  return []
}

const isBiWeeklyCode = (frequencyCode, frequencyLabel) => {
  const code = String(frequencyCode || '').trim().toUpperCase()
  if (code === 'BW' || code === 'BW_A' || code === 'BW_B') return true
  return String(frequencyLabel || '').trim().toLowerCase() === 'bi-weekly'
}

const rowWeekType = (row = {}) => {
  const explicit = String(row.weekType || '').trim().toUpperCase()
  if (explicit === 'A' || explicit === 'B') return explicit
  const code = String(row.frequencyCode || '').trim().toUpperCase()
  if (code === 'BW_A') return 'A'
  if (code === 'BW_B') return 'B'
  return null
}

const LONG_CYCLE_CODES = new Set(['M', 'M1', 'M2', 'M3', 'M4', 'Q', 'SA', 'A'])

const isLongCycleCode = (frequencyCode, frequencyLabel) => {
  const code = String(frequencyCode || '').trim().toUpperCase()
  if (LONG_CYCLE_CODES.has(code)) return true
  const label = String(frequencyLabel || '').trim().toLowerCase()
  return ['monthly', 'quarterly', 'semi-annually', 'annually'].includes(label)
}

const isDueOnDate = (row = {}, dateInput) => {
  const date = new Date(dateInput)
  const dayName = getDayName(date)
  const days = normalizeDaysForRow(row)
  const hasDayRule = days.length > 0
  if (hasDayRule && dayName && !days.includes(dayName)) return false

  if (isBiWeeklyCode(row.frequencyCode, row.frequency)) {
    const required = rowWeekType(row)
    if (!required) return false
    return getWeekTypeForDate(date) === required
  }

  // Long-cycle properties (monthly, quarterly, semi-annual, annual):
  // day-of-week alone is not enough — require an actual scheduledVisitDates match
  if (isLongCycleCode(row.frequencyCode, row.frequency)) {
    const dateStr = toYmd(date)
    const visits = Array.isArray(row.scheduledVisitDates) ? row.scheduledVisitDates : []
    if (!visits.length) return false
    return visits.some(v => {
      const vDate = String(v.date || v || '').slice(0, 10)
      return vDate === dateStr
    })
  }

  return true
}

const countDueOccurrences = (row = {}, dates = []) => {
  if (!Array.isArray(dates) || dates.length === 0) return 0
  return dates.reduce((sum, date) => sum + (isDueOnDate(row, date) ? 1 : 0), 0)
}

const areSameStringArrays = (left = [], right = []) => {
  if (left.length !== right.length) return false
  const l = [...left].map(item => String(item)).sort()
  const r = [...right].map(item => String(item)).sort()
  return l.every((item, idx) => item === r[idx])
}

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    overflowX: 'auto',
    paddingBottom: 2
  },
  toolbarDivider: {
    width: 1,
    height: 28,
    background: theme.palette.divider,
    flexShrink: 0
  },
  selectControl: {
    minWidth: 170
  },
  selectCompact: {
    minWidth: 130
  },
  legendGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 0, 1)
  },
  legendDot: {
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 4,
    verticalAlign: 'middle'
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 360px',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  mapPane: {
    minWidth: 0
  },
  sidePanel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1),
    maxHeight: 'calc(100vh - 260px)',
    minHeight: 520,
    overflowY: 'auto',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      maxHeight: 'none',
      minHeight: 'auto'
    }
  },
  sidebarCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: 'hidden',
    flexShrink: 0
  },
  sidebarCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 1.25),
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      background: '#f5f5f5'
    }
  },
  sidebarCardHeaderTitle: {
    fontWeight: 600,
    fontSize: '0.85rem'
  },
  sidebarCardContent: {
    padding: theme.spacing(1, 1.25, 1.25)
  },
  quickAssignCard: {
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 8,
    overflow: 'hidden',
    flexShrink: 0
  },
  quickAssignHeader: {
    background: theme.palette.primary.main,
    color: '#fff',
    padding: theme.spacing(0.75, 1.25),
    fontWeight: 600,
    fontSize: '0.85rem'
  },
  quickAssignContent: {
    padding: theme.spacing(1, 1.25, 1.25)
  },
  assignRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  applyButton: {
    fontWeight: 600,
    fontSize: '0.9rem'
  },
  pendingChip: {
    backgroundColor: '#FFF3E0',
    color: '#E65100',
    borderColor: '#FFB74D',
    fontSize: '0.72rem'
  },
  propertyName: {
    fontSize: '1.05rem',
    fontWeight: 700
  },
  propertyMeta: {
    fontSize: '0.78rem',
    color: '#777'
  },
  propertyInfoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: theme.spacing(0.5)
  },
  compactStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(0.5)
  },
  statsCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1),
    marginTop: theme.spacing(0.75)
  },
  statLabel: {
    fontSize: '0.72rem',
    color: '#666'
  },
  statValue: {
    fontSize: '0.82rem',
    fontWeight: 600
  },
  panelTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.75)
  },
  detailText: {
    fontSize: '0.82rem',
    color: '#555'
  },
  detailValue: {
    fontSize: '0.85rem',
    fontWeight: 600
  },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(0.75)
  },
  nearList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75)
  },
  routeBuilderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
    maxHeight: 'min(52vh, 430px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: theme.spacing(0.5),
    overscrollBehavior: 'contain',
    [theme.breakpoints.down('sm')]: {
      maxHeight: '45vh'
    }
  },
  nearItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(0.75)
  },
  nearHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  nearName: {
    fontSize: '0.82rem',
    fontWeight: 600
  },
  nearSub: {
    fontSize: '0.75rem',
    color: '#666'
  },
  emptyHint: {
    color: '#666',
    fontSize: '0.82rem'
  },
  impactRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    fontSize: '0.78rem',
    color: '#555',
    marginTop: theme.spacing(0.4)
  },
  impactValue: {
    fontWeight: 600,
    color: '#222'
  },
  impactBox: {
    background: '#f9f9f9',
    borderRadius: 6,
    padding: theme.spacing(0.75, 1),
    marginTop: theme.spacing(0.75)
  },
  warningText: {
    color: theme.palette.warning.main,
    fontWeight: 600
  },
  filterPopover: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 220
  },
  candidateItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(0.75),
    '&.dimmed': {
      opacity: 0.5
    }
  },
  candidateHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5)
  },
  candidateName: {
    fontSize: '0.82rem',
    fontWeight: 600,
    lineHeight: 1.2
  },
  candidateMeta: {
    fontSize: '0.72rem',
    color: '#666'
  },
  candidateActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5)
  },
  routeStopItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover }
  },
  routeStopOrder: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#999',
    minWidth: 18,
    paddingTop: 1
  },
  routeStopName: {
    fontSize: '0.78rem',
    fontWeight: 600,
    lineHeight: 1.2
  },
  routeStopDetail: {
    fontSize: '0.70rem',
    color: '#888',
    lineHeight: 1.3
  },
  routeStopMissing: {
    fontSize: '0.70rem',
    color: '#f44336',
    fontWeight: 600
  },
  deltaPositive: {
    color: theme.palette.error.main,
    fontWeight: 600,
    fontSize: '0.72rem'
  },
  deltaNegative: {
    color: theme.palette.success.main,
    fontWeight: 600,
    fontSize: '0.72rem'
  },
  toggleChips: {
    display: 'inline-flex',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    overflow: 'hidden',
    marginLeft: theme.spacing(1)
  },
  toggleChip: {
    padding: '2px 10px',
    fontSize: '0.72rem',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      background: '#eee'
    }
  },
  toggleChipActive: {
    padding: '2px 10px',
    fontSize: '0.72rem',
    cursor: 'pointer',
    userSelect: 'none',
    background: theme.palette.primary.main,
    color: '#fff'
  },
  bestFitList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    maxHeight: 'min(40vh, 340px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: theme.spacing(0.5),
    overscrollBehavior: 'contain'
  },
  bestFitItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(0.75),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75)
  },
  bestFitRank: {
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#999',
    minWidth: 18,
    textAlign: 'center',
    flexShrink: 0
  },
  bestFitInfo: {
    flex: 1,
    minWidth: 0
  },
  bestFitName: {
    fontSize: '0.82rem',
    fontWeight: 600,
    lineHeight: 1.25,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  bestFitMeta: {
    fontSize: '0.7rem',
    color: '#666',
    lineHeight: 1.3
  },
  capacityBar: {
    height: 6,
    borderRadius: 3,
    backgroundColor: '#e0e0e0',
    overflow: 'hidden',
    marginTop: 3
  },
  capacityBarFill: {
    height: '100%',
    borderRadius: 3,
    transition: 'width 0.3s'
  },
  heatmapTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.72rem',
    '& th': {
      fontWeight: 600,
      padding: '3px 2px',
      textAlign: 'center',
      color: '#666',
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    '& td': {
      padding: '3px 2px',
      textAlign: 'center',
      cursor: 'pointer',
      '&:hover': {
        background: '#f0f0f0'
      }
    }
  },
  heatmapRouteName: {
    textAlign: 'left',
    fontWeight: 500,
    maxWidth: 100,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    paddingRight: 4
  },
  heatmapDot: {
    display: 'inline-block',
    width: 14,
    height: 14,
    borderRadius: '50%',
    verticalAlign: 'middle'
  },
  reviewNavBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    background: '#f5f5f5',
    borderRadius: 8,
    marginBottom: theme.spacing(0.75),
    flexWrap: 'nowrap',
    overflowX: 'auto'
  },
  reviewNavProgress: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#555',
    whiteSpace: 'nowrap'
  },
  reviewNavLabel: {
    fontSize: '0.82rem',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  weekSummaryBox: {
    background: '#f8f9fa',
    borderRadius: 6,
    padding: theme.spacing(0.75, 1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5)
  },
  weekSummaryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.78rem',
    fontWeight: 600
  },
  weekSummaryFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.72rem',
    color: '#666',
    marginTop: 3
  },
  weekDayRow: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: 4,
    flexWrap: 'wrap'
  },
  weekDayChip: {
    fontSize: '0.68rem',
    color: '#555',
    padding: '1px 4px',
    borderRadius: 3,
    background: '#eee'
  },
  weekDayChipActive: {
    fontWeight: 700,
    color: '#222',
    background: '#e0e0e0'
  },
  weekDayChipOt: {
    color: '#f44336',
    fontWeight: 600
  }
}))

export default function MapTab ({ url, selectedScenarioId, crews, crewIds, routes, routeIds, onSnackbar }) {
  const classes = useStyles()
  const showAdvancedSidebar = false

  const [features, setFeatures] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(false)
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [legendOpen, setLegendOpen] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [routeContextOpen, setRouteContextOpen] = useState(true)
  const [showVipOnly, setShowVipOnly] = useState(false)
  const [showAnchorsOnly, setShowAnchorsOnly] = useState(false)
  const [showLockedOnly, setShowLockedOnly] = useState(false)
  const [showCentroids, setShowCentroids] = useState(true)
  const [mapRef, setMapRef] = useState(null)
  const [routeFilterId, setRouteFilterId] = useState('')

  const [selectedPropertyId, setSelectedPropertyId] = useState(null)
  const [assignmentRouteId, setAssignmentRouteId] = useState('')
  const [anchorCrewId, setAnchorCrewId] = useState('')
  const [applyingMove, setApplyingMove] = useState(false)
  const [applyingAnchor, setApplyingAnchor] = useState(false)
  const [suggestingCrew, setSuggestingCrew] = useState(false)
  const [nearestCrewSuggestion, setNearestCrewSuggestion] = useState(null)
  const [scenarioMetrics, setScenarioMetrics] = useState(null)
  const [loadingMetrics, setLoadingMetrics] = useState(false)
  const [focusCrewId, setFocusCrewId] = useState('')
  const [focusRouteId, setFocusRouteId] = useState('__ALL__')
  const [planningMode, setPlanningMode] = useState('day') // day | week | two-week
  const [focusDate, setFocusDate] = useState(() => toYmd(new Date()))
  const [contextWeekType, setContextWeekType] = useState('ALL') // ALL | A | B
  const [showDueOnly, setShowDueOnly] = useState(false)
  const [frequencyFocus, setFrequencyFocus] = useState('all') // all | weekly | biweekly
  const [focusContextStats, setFocusContextStats] = useState(null)
  const [loadingFocusContextStats, setLoadingFocusContextStats] = useState(false)
  const [scheduleDay, setScheduleDay] = useState('')
  const [scheduleWeekType, setScheduleWeekType] = useState('')
  const [focusedRoute, setFocusedRoute] = useState(null)
  const [loadingFocusedRoute, setLoadingFocusedRoute] = useState(false)
  const [hoursMode, setHoursMode] = useState('estimated') // 'estimated' | 'actual'
  const [routeBuilderOpen, setRouteBuilderOpen] = useState(true)
  const [candidatesDueOnly, setCandidatesDueOnly] = useState(false)
  const [showSuggestionPins, setShowSuggestionPins] = useState(false)
  const [showRouteStops, setShowRouteStops] = useState(true)
  const [showUnassigned, setShowUnassigned] = useState(false)
  const [showIrregularMtc, setShowIrregularMtc] = useState(false)
  const [fleetTodayOnly, setFleetTodayOnly] = useState(true)
  const [routeStopsOpen, setRouteStopsOpen] = useState(false)
  const [routeActionPending, setRouteActionPending] = useState(false)
  const [bestFitData, setBestFitData] = useState(null)
  const [bestFitLoading, setBestFitLoading] = useState(false)
  const [bestFitOpen, setBestFitOpen] = useState(true)
  const [heatmapOpen, setHeatmapOpen] = useState(false)
  const [capacityMatrix, setCapacityMatrix] = useState(null)
  const [capacityLoading, setCapacityLoading] = useState(false)
  const [fleetRoutes, setFleetRoutes] = useState([]) // array of { crewId, routeId, path: [{lat,lng}] }
  const [fleetRoutesLoading, setFleetRoutesLoading] = useState(false)
  const [showFleetRoutes, setShowFleetRoutes] = useState(true)
  const [approvedRouteDays, setApprovedRouteDays] = useState([])
  const [reviewNavOpen, setReviewNavOpen] = useState(false)
  const [reviewComboIdx, setReviewComboIdx] = useState(0)
  const [skipApproved, setSkipApproved] = useState(false)
  const [weeklyCrewStats, setWeeklyCrewStats] = useState(null)

  // Confirmation dialog state (replaces native confirm() for approved/locked moves)
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', type: '', details: null })
  const confirmResolverRef = useRef(null)

  const requestConfirmation = useCallback((title, message, type = 'generic', details = null) => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve
      setConfirmDialog({ open: true, title, message, type, details })
    })
  }, [])

  const handleConfirmDialogClose = useCallback((confirmed) => {
    setConfirmDialog(prev => ({ ...prev, open: false }))
    if (confirmResolverRef.current) {
      confirmResolverRef.current(confirmed)
      confirmResolverRef.current = null
    }
  }, [])

  const approvedSet = useMemo(() => {
    const s = new Set()
    approvedRouteDays.forEach(a => s.add(`${a.crewId}_${a.dayOfWeek}_${a.weekType || ''}`))
    return s
  }, [approvedRouteDays])

  const fetchApprovedRouteDays = useCallback(async () => {
    if (!selectedScenarioId) { setApprovedRouteDays([]); return }
    try {
      const resp = await axios.get(`${url}/api/hmi/territory/scenarios`)
      const scenario = (resp.data?.scenarios || []).find(s => s._id === selectedScenarioId)
      setApprovedRouteDays(scenario?.approvedRouteDays || [])
    } catch (_) { setApprovedRouteDays([]) }
  }, [selectedScenarioId, url])

  // Clear stale approvals immediately when scenario changes, then fetch fresh data
  useEffect(() => {
    setApprovedRouteDays([])
    fetchApprovedRouteDays()
  }, [fetchApprovedRouteDays])

  // Review Navigator: build all crew/day/weekType combos from capacityMatrix
  const reviewCombos = useMemo(() => {
    if (!capacityMatrix?.routes?.length) return []
    const weekOf = capacityMatrix.weekOf
    const combos = []
    const weekTypes = ['A', 'B']
    capacityMatrix.routes.forEach(route => {
      weekTypes.forEach(wt => {
        WORK_DAYS.forEach((dayName, dayIdx) => {
          // Compute a target date: find a Monday of the correct weekType near focusDate
          let targetDate = null
          if (weekOf) {
            const baseMonday = new Date(weekOf + 'T00:00:00')
            const baseWt = getWeekTypeForDate(baseMonday)
            // If we need the other week type, shift by 7 days
            const monday = baseWt === wt ? baseMonday : addDays(baseMonday, 7)
            targetDate = toYmd(addDays(monday, dayIdx))
          }
          const key = `${route.crewId}_${dayName}_${wt}`
          combos.push({
            crewId: route.crewId,
            routeId: route.routeId,
            routeName: route.routeName,
            dayName,
            weekType: wt,
            key,
            targetDate,
            approved: approvedSet.has(key)
          })
        })
      })
    })
    return combos
  }, [capacityMatrix, approvedSet])

  const filteredReviewCombos = useMemo(() => {
    if (!skipApproved) return reviewCombos
    return reviewCombos.filter(c => !c.approved)
  }, [reviewCombos, skipApproved])

  // Clamp index when filteredReviewCombos shrinks (e.g. after approving a combo in skip mode)
  useEffect(() => {
    if (filteredReviewCombos.length && reviewComboIdx >= filteredReviewCombos.length) {
      setReviewComboIdx(0)
    }
  }, [filteredReviewCombos.length, reviewComboIdx])

  const navigateToCombo = useCallback((combo) => {
    if (!combo) return
    if (combo.crewId) setFocusCrewId(combo.crewId)
    if (combo.routeId) setFocusRouteId(combo.routeId)
    if (combo.targetDate) setFocusDate(combo.targetDate)
  }, [])

  const reviewNavNext = useCallback(() => {
    if (!filteredReviewCombos.length) return
    const nextIdx = (reviewComboIdx + 1) % filteredReviewCombos.length
    setReviewComboIdx(nextIdx)
    navigateToCombo(filteredReviewCombos[nextIdx])
  }, [filteredReviewCombos, reviewComboIdx, navigateToCombo])

  const reviewNavPrev = useCallback(() => {
    if (!filteredReviewCombos.length) return
    const prevIdx = (reviewComboIdx - 1 + filteredReviewCombos.length) % filteredReviewCombos.length
    setReviewComboIdx(prevIdx)
    navigateToCombo(filteredReviewCombos[prevIdx])
  }, [filteredReviewCombos, reviewComboIdx, navigateToCombo])

  const fetchMapData = useCallback(async () => {
    setLoading(true)
    try {
      const endpoint = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/map-data`
        : `${url}/api/hmi/territory/map-data`
      const response = await axios.get(endpoint)
      setFeatures(response.data?.featureCollection?.features || [])
      setMeta(response.data?.meta || null)
    } catch (err) {
      console.error('Error loading map data:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to load map data', 'error')
    } finally {
      setLoading(false)
    }
  }, [url, selectedScenarioId, onSnackbar])

  useEffect(() => {
    fetchMapData()
  }, [fetchMapData])

  const fetchScenarioMetrics = useCallback(async () => {
    if (!selectedScenarioId) {
      setScenarioMetrics(null)
      return
    }
    setLoadingMetrics(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/metrics`)
      setScenarioMetrics(response.data || null)
    } catch (err) {
      console.error('Error loading scenario metrics for map sidebar:', err)
      setScenarioMetrics(null)
    } finally {
      setLoadingMetrics(false)
    }
  }, [selectedScenarioId, url])

  useEffect(() => {
    fetchScenarioMetrics()
  }, [fetchScenarioMetrics])

  const fetchFocusedRoute = useCallback(async () => {
    if (!focusCrewId || !focusDate) {
      setFocusedRoute(null)
      return
    }
    setLoadingFocusedRoute(true)
    try {
      const routeUrl = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${focusCrewId}/route`
        : `${url}/api/hmi/territory/crews/${focusCrewId}/route`
      const response = await axios.get(routeUrl, { params: { date: focusDate } })
      setFocusedRoute(response.data || null)
    } catch (err) {
      console.error('Error loading focused crew route:', err)
      setFocusedRoute(null)
    } finally {
      setLoadingFocusedRoute(false)
    }
  }, [focusCrewId, focusDate, selectedScenarioId, url])

  useEffect(() => {
    fetchFocusedRoute()
  }, [fetchFocusedRoute])

  const contextDates = useMemo(() => {
    const anchorDate = parseYmd(focusDate)
    if (planningMode === 'day') {
      return [anchorDate]
    }

    const weekStart = getMonday(anchorDate)
    const weekDates = WORK_DAYS.map((_, idx) => addDays(weekStart, idx))

    if (planningMode === 'week') {
      return weekDates
    }

    const nextWeekStart = addDays(weekStart, 7)
    const nextWeekDates = WORK_DAYS.map((_, idx) => addDays(nextWeekStart, idx))
    return [...weekDates, ...nextWeekDates]
  }, [focusDate, planningMode])

  const filteredContextDates = useMemo(() => {
    if (contextWeekType === 'ALL') return contextDates
    return contextDates.filter(date => getWeekTypeForDate(date) === contextWeekType)
  }, [contextDates, contextWeekType])

  const fetchFocusContextStats = useCallback(async () => {
    if (!focusCrewId || !filteredContextDates.length) {
      setFocusContextStats(null)
      return
    }

    setLoadingFocusContextStats(true)
    try {
      // Always use the /route endpoint — in scenario mode it reads from the
      // snapshot (single source of truth) and returns per-stop data including
      // medianServiceMinutes needed for actual mode.
      const routeBase = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${focusCrewId}/route`
        : `${url}/api/hmi/territory/crews/${focusCrewId}/route`

      const responses = await Promise.all(
        filteredContextDates.map(date =>
          axios.get(routeBase, { params: { date: toYmd(date) } })
            .catch(() => ({ data: null }))
        )
      )

      const days = responses.map((res, idx) => {
        const data = res.data || {}
        const stops = data.stops || []
        const date = toYmd(filteredContextDates[idx])
        const serviceMin = stops.reduce((sum, s) => sum + Math.round(Number(s.serviceMinutes || 0)), 0)
        const driveMin = stops.reduce((sum, s) => sum + Math.round(Number(s.driveTimeMinutes || 0)), 0)
        const totalMin = serviceMin + driveMin
        const otMin = Math.max(0, totalMin - DAILY_OT_THRESHOLD_MIN)
        return {
          date,
          dayName: getDayName(parseYmd(date)),
          weekType: getWeekTypeForDate(parseYmd(date)),
          stops: stops.length,
          serviceMin,
          driveMin,
          totalMin,
          otMin,
          mode: data.meta?.mode || 'live'
        }
      })

      const summary = days.reduce((acc, day) => {
        acc.stops += day.stops
        acc.serviceMin += day.serviceMin
        acc.driveMin += day.driveMin
        acc.totalMin += day.totalMin
        acc.otMin += day.otMin
        return acc
      }, {
        stops: 0,
        serviceMin: 0,
        driveMin: 0,
        totalMin: 0,
        otMin: 0
      })

      setFocusContextStats({ days, summary })
    } catch (err) {
      console.error('Error loading focused context stats:', err)
      setFocusContextStats(null)
    } finally {
      setLoadingFocusContextStats(false)
    }
  }, [filteredContextDates, focusCrewId, selectedScenarioId, url])

  useEffect(() => {
    fetchFocusContextStats()
  }, [fetchFocusContextStats])

  // --- Weekly OT Summary: fetch Mon-Fri stats for the focused crew ---
  const fetchWeeklyCrewStats = useCallback(async () => {
    if (!focusCrewId) {
      setWeeklyCrewStats(null)
      return
    }
    const weekStart = getMonday(parseYmd(focusDate))
    const weekDates = WORK_DAYS.map((_, idx) => addDays(weekStart, idx))

    try {
      // Always use the /route endpoint — it returns per-stop medianServiceMinutes
      // needed for actual mode. The /compare endpoint only returns aggregates.
      const routeBase = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${focusCrewId}/route`
        : `${url}/api/hmi/territory/crews/${focusCrewId}/route`

      const responses = await Promise.all(
        weekDates.map(date =>
          axios.get(routeBase, { params: { date: toYmd(date) } })
            .catch(() => ({ data: null }))
        )
      )

      const days = responses.map((res, idx) => {
        const data = res.data || {}
        const stops = data.stops || []
        const date = toYmd(weekDates[idx])
        const serviceMin = stops.reduce((sum, s) => sum + Math.round(Number(s.serviceMinutes || 0)), 0)
        const actualServiceMin = stops.reduce((sum, s) => {
          const v = s.medianServiceMinutes > 0 ? s.medianServiceMinutes : Math.round(Number(s.serviceMinutes || 0))
          return sum + v
        }, 0)
        const driveMin = stops.reduce((sum, s) => sum + Math.round(Number(s.driveTimeMinutes || 0)), 0)
        const totalMin = serviceMin + driveMin
        const actualTotalMin = actualServiceMin + driveMin
        return {
          date,
          dayName: getDayName(weekDates[idx]),
          stops: stops.length,
          serviceMin,
          actualServiceMin,
          driveMin,
          totalMin,
          actualTotalMin,
          otMin: Math.max(0, totalMin - DAILY_OT_THRESHOLD_MIN),
          actualOtMin: Math.max(0, actualTotalMin - DAILY_OT_THRESHOLD_MIN)
        }
      })

      const agg = days.reduce((a, d) => {
        a.weekServiceMin += d.serviceMin; a.weekDriveMin += d.driveMin; a.weekTotalMin += d.totalMin; a.weekOtMin += d.otMin
        a.weekActualServiceMin += d.actualServiceMin; a.weekActualTotalMin += d.actualTotalMin; a.weekActualOtMin += d.actualOtMin
        return a
      }, { weekServiceMin: 0, weekDriveMin: 0, weekTotalMin: 0, weekOtMin: 0, weekActualServiceMin: 0, weekActualTotalMin: 0, weekActualOtMin: 0 })
      setWeeklyCrewStats({ days, ...agg })
    } catch (err) {
      console.error('Error loading weekly crew stats:', err)
      setWeeklyCrewStats(null)
    }
  }, [focusCrewId, focusDate, selectedScenarioId, url])

  useEffect(() => {
    fetchWeeklyCrewStats()
  }, [fetchWeeklyCrewStats])

  useEffect(() => {
    setWeeklyCrewStats(null)
  }, [focusCrewId])

  const availableRoutes = useMemo(() => {
    if (Array.isArray(routes) && routes.length) {
      return routes
        .map(item => ({ routeId: String(item._id), routeName: item.name || String(item._id), crewId: item.crewId ? String(item.crewId) : null }))
        .sort((a, b) => a.routeName.localeCompare(b.routeName))
    }
    return []
  }, [routes])

  const availableCrews = useMemo(() => {
    if (Array.isArray(crews) && crews.length) {
      return crews
        .map(item => ({ crewId: String(item._id), crewName: item.name || item.foremanName || String(item._id) }))
        .sort((a, b) => a.crewName.localeCompare(b.crewName))
    }

    const centroidCrewMap = new Map()
    features
      .filter(item => item.properties?.featureType === 'crewCentroid')
      .forEach(item => {
        const crewId = String(item.properties?.crewId || '')
        if (!crewId) return
        centroidCrewMap.set(crewId, item.properties?.crewName || crewId)
      })

    return Array.from(centroidCrewMap.entries())
      .map(([crewId, crewName]) => ({ crewId, crewName }))
      .sort((a, b) => a.crewName.localeCompare(b.crewName))
  }, [crews, features])

  // For dropdowns: prefer routes, fallback to crews
  const assignmentOptions = useMemo(() => {
    if (availableRoutes.length) return availableRoutes.map(r => ({ id: r.routeId, name: r.routeName }))
    return availableCrews.map(c => ({ id: c.crewId, name: c.crewName }))
  }, [availableRoutes, availableCrews])

  // Name lookup by route/crew ID for resolving date-specific override names
  const nameById = useMemo(() => {
    const map = new Map()
    availableRoutes.forEach(r => { map.set(r.routeId, r.routeName) })
    availableCrews.forEach(c => { map.set(c.crewId, c.crewName) })
    return map
  }, [availableRoutes, availableCrews])

  const isRouteMode = availableRoutes.length > 0
  const isFleetMode = focusRouteId === '__ALL__'

  useEffect(() => {
    if (focusRouteId === '__ALL__') return
    if (isRouteMode && availableRoutes.length) {
      if (focusRouteId && availableRoutes.some(r => r.routeId === focusRouteId)) return
      // Previously-selected route no longer valid — fall back to fleet mode
      setFocusRouteId('__ALL__')
      setFocusCrewId('')
      return
    }
    if (!availableCrews.length) {
      setFocusCrewId('')
      setFocusRouteId('__ALL__')
      return
    }
    if (focusCrewId && availableCrews.some(item => item.crewId === focusCrewId)) return
    // Previously-selected crew no longer valid — fall back to fleet mode
    setFocusRouteId('__ALL__')
    setFocusCrewId('')
  }, [availableCrews, availableRoutes, focusCrewId, focusRouteId, isRouteMode])

  // Resolve per-date crew/route from futureVisitCrews (Aspire one-off assignments)
  const effectiveCrewByProperty = useMemo(() => {
    if (!focusDate) return new Map()
    const dateStr = focusDate // already YYYY-MM-DD
    const map = new Map()
    features.forEach(f => {
      const p = f.properties
      if (p?.featureType !== 'property' || !p.futureVisitCrews?.length) return
      const match = p.futureVisitCrews.find(v => v.date === dateStr)
      if (match) {
        map.set(Number(p.aspirePropertyId), {
          crewId: match.crewId || null,
          routeId: match.routeId || null
        })
      }
    })
    return map
  }, [features, focusDate])

  // Track on-route property IDs (must be before propertyFeatures for showRouteStops filter)
  const routeStopIds = useMemo(() => {
    const stops = focusedRoute?.stops || []
    return new Set(stops.map(s => Number(s.aspirePropertyId)).filter(Number.isFinite))
  }, [focusedRoute])

  const propertyFeatures = useMemo(() => {
    let props = features.filter(f => f.properties?.featureType === 'property')

    // 1. Attribute filters
    if (showVipOnly) props = props.filter(f => f.properties.isVip)
    if (showAnchorsOnly) props = props.filter(f => f.properties.isAnchor)
    if (showLockedOnly) props = props.filter(f => f.properties.isLocked)

    // Helper: resolve effective crew/route for a property considering date-specific overrides
    const getEffective = (p) => {
      const dateOverride = effectiveCrewByProperty.get(Number(p.aspirePropertyId))
      return {
        routeId: dateOverride?.routeId || p.assignedRouteId || null,
        crewId: dateOverride?.crewId || p.assignedCrewId || null
      }
    }

    // 2. Legend click-filter
    if (routeFilterId === 'unassigned') {
      props = props.filter(f => {
        const eff = getEffective(f.properties)
        return !eff.routeId && !eff.crewId
      })
    } else if (routeFilterId) {
      props = props.filter(f => {
        const eff = getEffective(f.properties)
        return String(eff.routeId || eff.crewId || '') === String(routeFilterId)
      })
    }

    // 3. Frequency focus
    if (frequencyFocus === 'weekly') {
      props = props.filter(f => !isBiWeeklyCode(f.properties?.frequencyCode, f.properties?.frequency))
    }
    if (frequencyFocus === 'biweekly') {
      props = props.filter(f => isBiWeeklyCode(f.properties?.frequencyCode, f.properties?.frequency))
    }

    // 4. Due in context
    if (showDueOnly && filteredContextDates.length) {
      props = props.filter(f => filteredContextDates.some(date => isDueOnDate(f.properties || {}, date)))
    }

    // 5. Irregular Mtc filter — when OFF, hide long-cycle properties UNLESS scheduled for a focused date
    if (!showIrregularMtc) {
      props = props.filter(f => {
        const p = f.properties || {}
        if (!isLongCycleCode(p.frequencyCode, p.frequency)) return true
        // Keep if scheduled for any of the focused context dates
        const visits = Array.isArray(p.scheduledVisitDates) ? p.scheduledVisitDates : []
        if (!visits.length || !filteredContextDates.length) return false
        return filteredContextDates.some(date => {
          const dateStr = toYmd(date)
          return visits.some(v => String(v.date || v || '').slice(0, 10) === dateStr)
        })
      })
    }

    // 6. Mode-dependent visibility (uses date-specific crew/route via getEffective)
    if (isFleetMode) {
      if (fleetTodayOnly && focusDate) {
        const focusDateObj = parseYmd(focusDate)
        props = props.filter(f => {
          const p = f.properties || {}
          const eff = getEffective(p)
          const isAssigned = !!(eff.routeId || eff.crewId)
          const isUnassigned = !eff.routeId && !eff.crewId
          if (isAssigned && isDueOnDate(p, focusDateObj)) return true
          if (showUnassigned && isUnassigned && !isLongCycleCode(p.frequencyCode, p.frequency)) return true
          return false
        })
      } else {
        // Show all assigned; optionally add unassigned regular-freq
        props = props.filter(f => {
          const p = f.properties || {}
          const eff = getEffective(p)
          const isAssigned = !!(eff.routeId || eff.crewId)
          if (isAssigned) return true
          if (showUnassigned && !isLongCycleCode(p.frequencyCode, p.frequency)) return true
          return false
        })
      }
    } else if (focusCrewId) {
      if (showRouteStops) {
        // Show route stops for today + crew-assigned properties that are due in context + optionally unassigned regular-freq
        props = props.filter(f => {
          const p = f.properties || {}
          const isOnRoute = routeStopIds.size > 0 && routeStopIds.has(Number(p.aspirePropertyId))
          if (isOnRoute) return true
          const eff = getEffective(p)
          const isAssignedToFocusCrew = String(eff.crewId || '') === String(focusCrewId)
          if (isAssignedToFocusCrew) {
            // Only show crew-assigned properties that are due on one of the context dates
            if (!filteredContextDates.length) return true
            return filteredContextDates.some(date => isDueOnDate(p, date))
          }
          const isUnassigned = !eff.routeId && !eff.crewId
          if (showUnassigned && isUnassigned && !isLongCycleCode(p.frequencyCode, p.frequency)) return true
          return false
        })
      } else {
        // Show ALL properties (all crews) + optionally unassigned regular-freq
        props = props.filter(f => {
          const p = f.properties || {}
          const eff = getEffective(p)
          const isAssigned = !!(eff.routeId || eff.crewId)
          if (isAssigned) return true
          if (showUnassigned && !isLongCycleCode(p.frequencyCode, p.frequency)) return true
          return false
        })
      }
    }

    return props
  }, [
    effectiveCrewByProperty,
    focusCrewId,
    focusDate,
    fleetTodayOnly,
    isFleetMode,
    routeFilterId,
    features,
    filteredContextDates,
    frequencyFocus,
    routeStopIds,
    showAnchorsOnly,
    showDueOnly,
    showIrregularMtc,
    showLockedOnly,
    showRouteStops,
    showUnassigned,
    showVipOnly
  ])

  const allPropertyFeatures = useMemo(
    () => features.filter(f => f.properties?.featureType === 'property'),
    [features]
  )

  const centroidFeatures = useMemo(() => {
    if (!showCentroids) return []
    if (routeFilterId === 'unassigned') return []
    let centroids = features.filter(f => f.properties?.featureType === 'routeCentroid' || f.properties?.featureType === 'crewCentroid')
    if (routeFilterId) centroids = centroids.filter(f => String(f.properties.routeId || f.properties.crewId || '') === String(routeFilterId))
    return centroids
  }, [features, showCentroids, routeFilterId])

  const selectedPropertyFeature = useMemo(
    () => allPropertyFeatures.find(f => Number(f.properties?.aspirePropertyId) === Number(selectedPropertyId)) || null,
    [allPropertyFeatures, selectedPropertyId]
  )

  const allCentroidFeatures = useMemo(
    () => features.filter(f => f.properties?.featureType === 'routeCentroid' || f.properties?.featureType === 'crewCentroid'),
    [features]
  )

  const centroidByCrewId = useMemo(() => {
    const map = new Map()
    allCentroidFeatures.forEach(feature => {
      const id = String(feature.properties?.routeId || feature.properties?.crewId || '')
      const [lng, lat] = feature.geometry?.coordinates || []
      if (!id || !Number.isFinite(lat) || !Number.isFinite(lng)) return
      map.set(id, { lat, lng })
    })
    return map
  }, [allCentroidFeatures])

  const crewMetricsById = useMemo(() => {
    const map = new Map()
    // Prefer route-level metrics, fallback to crew-level
    const routesList = scenarioMetrics?.routes || []
    if (routesList.length) {
      routesList.forEach(item => {
        if (!item?.routeId) return
        map.set(String(item.routeId), item)
      })
    }
    const crewsList = scenarioMetrics?.crews || []
    crewsList.forEach(item => {
      if (!item?.crewId || item.crewId === 'unassigned') return
      if (!map.has(String(item.crewId))) {
        map.set(String(item.crewId), item)
      }
    })
    return map
  }, [scenarioMetrics])

  const fleetDayStats = useMemo(() => {
    if (!isFleetMode || !capacityMatrix?.routes?.length || !focusDate) return null
    const focusDayName = getDayName(parseYmd(focusDate))
    if (!focusDayName || !WORK_DAYS.includes(focusDayName)) return null

    let totalStops = 0, totalServiceMin = 0, totalDriveMin = 0, totalTotalMin = 0, totalOtMin = 0
    const crewDays = []

    capacityMatrix.routes.forEach(route => {
      const dm = route.days?.[focusDayName]
      if (!dm) return
      totalStops += dm.stops || 0
      totalServiceMin += dm.serviceMin || 0
      totalDriveMin += dm.driveMin || 0
      totalTotalMin += dm.totalMin || 0
      totalOtMin += dm.otMin || 0
      crewDays.push({ routeId: route.routeId, routeName: route.routeName, crewId: route.crewId, ...dm })
    })
    crewDays.sort((a, b) => (b.totalMin || 0) - (a.totalMin || 0))

    return {
      dayName: focusDayName, date: focusDate, crewCount: crewDays.length,
      totalStops, totalServiceMin, totalDriveMin, totalTotalMin, totalOtMin,
      crewsInOt: crewDays.filter(c => (c.otMin || 0) > 0).length,
      crewDays
    }
  }, [isFleetMode, capacityMatrix, focusDate])

  const fleetAvgWeeklyMinutes = useMemo(
    () => Number(scenarioMetrics?.fleet?.avgCrewLoad || 0),
    [scenarioMetrics]
  )

  // Lookup map: routeId or crewId → day stats for focusDate (used by Route Builder candidate cards)
  const capacityByRoute = useMemo(() => {
    const map = new Map()
    if (!capacityMatrix?.routes?.length || !focusDate) return map
    const dayName = getDayName(parseYmd(focusDate))
    if (!dayName || !WORK_DAYS.includes(dayName)) return map
    capacityMatrix.routes.forEach(route => {
      const dm = route.days?.[dayName]
      if (!dm) return
      const entry = { ...dm, routeName: route.routeName, routeId: route.routeId, crewId: route.crewId }
      if (route.routeId) map.set(String(route.routeId), entry)
      if (route.crewId) map.set(String(route.crewId), entry)
    })
    return map
  }, [capacityMatrix, focusDate])

  useEffect(() => {
    if (isFleetMode) setLegendOpen(true)
  }, [isFleetMode])

  useEffect(() => {
    if (!selectedPropertyId) return
    if (selectedPropertyFeature) return
    setSelectedPropertyId(null)
  }, [selectedPropertyFeature, selectedPropertyId])

  useEffect(() => {
    if (!selectedPropertyFeature) {
      setAssignmentRouteId('')
      setAnchorCrewId('')
      setScheduleDay('')
      setScheduleWeekType('')
      setNearestCrewSuggestion(null)
      return
    }

    const props = selectedPropertyFeature.properties || {}
    const dateOvr = effectiveCrewByProperty.get(Number(props.aspirePropertyId))
    const effRouteId = dateOvr?.routeId || props.assignedRouteId
    const effCrewId = dateOvr?.crewId || props.assignedCrewId
    const assignedId = effRouteId
      ? String(effRouteId)
      : (effCrewId ? String(effCrewId) : '')
    setAssignmentRouteId(assignedId)
    setAnchorCrewId(assignedId)
    setScheduleDay('')
    setScheduleWeekType('')
    setNearestCrewSuggestion(null)
  }, [effectiveCrewByProperty, selectedPropertyFeature])

  const nearbyProperties = useMemo(() => {
    if (!selectedPropertyFeature) return []

    const [selectedLng, selectedLat] = selectedPropertyFeature.geometry.coordinates
    if (!Number.isFinite(selectedLat) || !Number.isFinite(selectedLng)) return []

    return allPropertyFeatures
      .filter(item => Number(item.properties?.aspirePropertyId) !== Number(selectedPropertyFeature.properties?.aspirePropertyId))
      .map(item => {
        const [lng, lat] = item.geometry.coordinates
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
        const distanceMiles = haversineMiles(selectedLat, selectedLng, lat, lng)
        return {
          feature: item,
          distanceMiles
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.distanceMiles - b.distanceMiles)
      .slice(0, 10)
  }, [allPropertyFeatures, selectedPropertyFeature])

  const routeLegend = useMemo(() => {
    const legendMap = new Map()
    propertyFeatures.forEach(f => {
      const p = f.properties
      const dateOverride = effectiveCrewByProperty.get(Number(p.aspirePropertyId))
      const rid = dateOverride?.routeId || p.assignedRouteId
      if (rid && !legendMap.has(rid)) {
        legendMap.set(rid, nameById.get(String(rid)) || p.assignedRouteName || rid)
      } else if (!rid) {
        // Fallback to crewId for older data
        const cid = dateOverride?.crewId || p.assignedCrewId
        if (cid && !legendMap.has(cid)) {
          legendMap.set(cid, nameById.get(String(cid)) || p.assignedCrewName || cid)
        }
      }
    })
    return Array.from(legendMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
  }, [effectiveCrewByProperty, nameById, propertyFeatures])

  const activeSecondaryFilterCount = useMemo(() => {
    let count = 0
    if (showVipOnly) count++
    if (showAnchorsOnly) count++
    if (showLockedOnly) count++
    if (!showCentroids) count++
    if (contextWeekType !== 'ALL') count++
    if (frequencyFocus !== 'all') count++
    if (showDueOnly) count++
    return count
  }, [showVipOnly, showAnchorsOnly, showLockedOnly, showCentroids, contextWeekType, frequencyFocus, showDueOnly])

  const getMarkerIcon = useCallback((feature) => {
    const props = feature.properties
    // Use date-specific crew/route from futureVisitCrews when available
    const dateOverride = effectiveCrewByProperty.get(Number(props.aspirePropertyId))
    const effectiveRouteId = dateOverride?.routeId || props.assignedRouteId
    const effectiveCrewId = dateOverride?.crewId || props.assignedCrewId
    const crewColor = effectiveRouteId
      ? getRouteColor(effectiveRouteId, routeIds)
      : (effectiveCrewId ? getCrewColor(effectiveCrewId, crewIds) : '#9E9E9E')

    const isSelected = Number(props.aspirePropertyId) === Number(selectedPropertyId)
    const isOnRoute = routeStopIds.size > 0 && routeStopIds.has(Number(props.aspirePropertyId))
    const hasActiveRoute = routeStopIds.size > 0

    const baseScale = props.isAnchor ? 12 : (props.isVip ? 10 : 8)
    let scale = isSelected ? baseScale + 4 : (isOnRoute ? baseScale + 2 : baseScale)
    let strokeColor = isSelected ? '#1976D2' : (isOnRoute ? '#00BCD4' : (props.isLocked ? '#FFD600' : '#ffffff'))
    let strokeWeight = isSelected ? 4 : (isOnRoute ? 3 : (props.isLocked ? 3 : 1.5))
    let fillOpacity = isSelected ? 1 : (hasActiveRoute && !isOnRoute ? 0.45 : 0.85)

    return {
      path: window.google?.maps?.SymbolPath?.CIRCLE,
      scale,
      fillColor: crewColor,
      fillOpacity,
      strokeColor,
      strokeWeight
    }
  }, [crewIds, effectiveCrewByProperty, routeIds, routeStopIds, selectedPropertyId])

  const onMapLoad = useCallback((map) => {
    setMapRef(map)
  }, [])

  const handlePropertySelect = useCallback((feature) => {
    if (!feature || feature.properties?.featureType !== 'property') return
    setSelectedPropertyId(Number(feature.properties.aspirePropertyId))
  }, [])

  // Helper: post a move and handle 409 conflicts (approved route day, locked property) with confirmation dialog + retry
  const postMoveWithApprovalCheck = async (moveUrl, payload) => {
    try {
      const resp = await axios.post(moveUrl, payload)
      console.log('[postMoveWithApprovalCheck]  resp', resp)
      return resp
    } catch (err) {
      console.log('[postMoveWithApprovalCheck] ERrro', err)
      if (err.response?.status === 409 && err.response?.data?.code === 'APPROVED_ROUTE_DAY') {
        const data = err.response.data
        const confirmed = await requestConfirmation(
          'Approved Route Day',
          data.message || 'This move affects an approved route day.',
          'approved',
          { fromApproved: data.fromApproved, toApproved: data.toApproved }
        )
        if (!confirmed) {
          if (onSnackbar) onSnackbar('Move cancelled — approved route was not changed', 'info')
          throw new Error('Move cancelled by user')
        }
        const resp = await axios.post(moveUrl, { ...payload, force: true })
        fetchApprovedRouteDays()
        return resp
      }
      if (err.response?.status === 409 && err.response?.data?.code === 'LOCKED_PROPERTY') {
        const lockedName = err.response.data.lockedCrewName || 'another crew'
        const confirmed = await requestConfirmation(
          'Locked Property',
          `This property is locked to ${lockedName}.`,
          'locked',
          { lockedCrewName: lockedName }
        )
        if (!confirmed) {
          if (onSnackbar) onSnackbar('Move cancelled — locked property was not changed', 'info')
          throw new Error('Move cancelled by user')
        }
        return axios.post(moveUrl, { ...payload, force: true })
      }
      throw err
    }
  }

  const moveSelectedProperty = async () => {
    if (!selectedScenarioId) {
      if (onSnackbar) onSnackbar('Pick a draft scenario first to move properties', 'warning')
      return
    }

    if (!selectedPropertyFeature) return

    if (!hasAnyMoveChange) {
      if (onSnackbar) onSnackbar('No assignment or schedule change to apply', 'info')
      return
    }

    setApplyingMove(true)
    try {
      const aspirePropertyId = Number(selectedPropertyFeature.properties?.aspirePropertyId)
      const payload = {
        aspirePropertyId,
        moveReason: 'map_manual'
      }
      if (isRouteMode) {
        payload.toRouteId = assignmentRouteId || null
      } else {
        payload.toCrewId = assignmentRouteId || null
      }
      if (hasDayUpdate) payload.toDaysOfWeek = scheduleDay ? [scheduleDay] : []
      if (hasWeekTypeUpdate) payload.toWeekType = scheduleWeekType === 'ANY' ? null : scheduleWeekType

      const resp = await postMoveWithApprovalCheck(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`, payload)
      const snapCount = (resp?.data?.snapshotsAffected?.pulled || 0) + (resp?.data?.snapshotsAffected?.pushed || 0)
      const snapSuffix = snapCount > 0 ? ` (${snapCount} snapshots updated)` : ''

      if (onSnackbar) {
        const toName = assignmentRouteId
          ? (assignmentOptions.find(o => o.id === assignmentRouteId)?.name || 'route')
          : 'Unassigned'
        if (hasCrewUpdate && (hasDayUpdate || hasWeekTypeUpdate)) {
          onSnackbar(`Assignment + schedule updated (${toName})` + snapSuffix, 'success')
        } else if (hasCrewUpdate) {
          onSnackbar(
            (assignmentRouteId
              ? `Property reassigned to ${toName}`
              : 'Property unassigned') + snapSuffix,
            'success'
          )
        } else {
          onSnackbar('Property schedule updated' + snapSuffix, 'success')
        }
      }

      setScheduleDay('')
      setScheduleWeekType('')

      await Promise.all([
        fetchMapData(),
        fetchScenarioMetrics(),
        fetchFocusContextStats(),
        fetchFocusedRoute()
      ])
    } catch (err) {
      if (err.message === 'Move cancelled by user') { setApplyingMove(false); return }
      console.error('Error moving property from map:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to move property', 'error')
    } finally {
      setApplyingMove(false)
    }
  }

  const assignAsAnchor = async (isAnchor) => {
    if (!selectedPropertyFeature) return
    if (isAnchor && !selectedWeeklyAnchorEligible) {
      if (onSnackbar) onSnackbar(`Only weekly properties can be anchors (this is ${selectedFrequencyLabel})`, 'warning')
      return
    }

    const propertyId = Number(selectedPropertyFeature.properties?.aspirePropertyId)
    if (isAnchor && !anchorCrewId) {
      if (onSnackbar) onSnackbar('Choose an anchor crew first', 'warning')
      return
    }

    setApplyingAnchor(true)
    try {
      await axios.patch(`${url}/api/hmi/territory/anchors/${propertyId}`, {
        isAnchor: !!isAnchor,
        anchorCrewId: isAnchor ? anchorCrewId : null
      })
      if (onSnackbar) onSnackbar(isAnchor ? 'Anchor assigned' : 'Anchor removed', 'success')
      await fetchMapData()
    } catch (err) {
      console.error('Error updating anchor from map:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to update anchor', 'error')
    } finally {
      setApplyingAnchor(false)
    }
  }

  const suggestNearestCrew = async () => {
    if (!selectedScenarioId || !selectedPropertyFeature) {
      if (onSnackbar) onSnackbar('Select a draft scenario to use nearest-crew suggestion', 'warning')
      return
    }

    const aspirePropertyId = Number(selectedPropertyFeature.properties?.aspirePropertyId)

    setSuggestingCrew(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/suggest-nearest`, {
        aspirePropertyIds: [aspirePropertyId],
        includeAssigned: true,
        limit: 1
      })
      const suggestion = response.data?.suggestions?.[0] || null
      setNearestCrewSuggestion(suggestion)

      const suggestedId = suggestion?.suggestedRouteId || suggestion?.suggestedCrewId
      const suggestedName = suggestion?.suggestedRouteName || suggestion?.suggestedCrewName

      if (suggestedId) {
        setAssignmentRouteId(String(suggestedId))
        if (!anchorCrewId) setAnchorCrewId(String(suggestedId))

        const distLabel = Number.isFinite(Number(suggestion.driveMinutes))
          ? `${Math.round(Number(suggestion.driveMinutes))} min`
          : `${Number(suggestion.distanceMiles || 0).toFixed(2)} mi`

        if (onSnackbar) onSnackbar(`Suggested ${suggestedName || 'route'} (${distLabel})`, 'success')
      } else if (onSnackbar) {
        onSnackbar('No nearest route suggestion available', 'info')
      }
    } catch (err) {
      console.error('Error suggesting nearest crew from map:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to suggest nearest crew', 'error')
    } finally {
      setSuggestingCrew(false)
    }
  }

  // Phase 3C: Service time helper (uses hoursMode toggle)
  // All times (property-level and crew-level) are already wall-clock minutes (pre-divided by crew size on server).
  const getServiceMinutes = useCallback((props) => {
    if (!props) return 0
    if (hoursMode === 'actual' && (props.medianOnSiteMinutes || props.medianOnSiteHours)) {
      // medianOnSiteMinutes is wall-clock (man-hours ÷ crew size), computed on server
      return props.medianOnSiteMinutes || Math.round((props.medianOnSiteHours || 0) * 60)
    }
    // estimatedMinutesOnSite is already wall-clock time per property
    return Number(props.estimatedMinutesOnSite || 0) || Math.round((props.estimatedHours || 0) * 60)
  }, [hoursMode])

  const selectedProps = selectedPropertyFeature?.properties || null
  const selectedDateOverride = selectedProps
    ? effectiveCrewByProperty.get(Number(selectedProps.aspirePropertyId))
    : null
  const selectedEffectiveRouteId = selectedDateOverride?.routeId || selectedProps?.assignedRouteId || null
  const selectedEffectiveCrewId = selectedDateOverride?.crewId || selectedProps?.assignedCrewId || null
  const selectedAssignedId = selectedEffectiveRouteId
    ? String(selectedEffectiveRouteId)
    : (selectedEffectiveCrewId ? String(selectedEffectiveCrewId) : '')
  const selectedEffectiveName = selectedDateOverride
    ? (nameById.get(String(selectedEffectiveRouteId)) || nameById.get(String(selectedEffectiveCrewId)) || selectedProps?.assignedRouteName || selectedProps?.assignedCrewName || 'Unassigned')
    : (selectedProps?.assignedRouteName || selectedProps?.assignedCrewName || 'Unassigned')
  const selectedServiceMinutes = getServiceMinutes(selectedProps)
  const focusRouteName = isRouteMode
    ? (availableRoutes.find(r => r.routeId === focusRouteId)?.routeName || 'Route')
    : (availableCrews.find(item => item.crewId === focusCrewId)?.crewName || 'Crew')
  const focusCrewName = focusRouteName

  // Phase 4D: "Add to Day" action for route builder
  const addToDay = async (aspirePropertyId) => {
    console.log('addToDay...', aspirePropertyId)
    if (!selectedScenarioId || !focusCrewId || !focusDate) {
      if (onSnackbar) onSnackbar('Select a scenario, crew, and date first', 'warning')
      return
    }
    const dayName = getDayName(parseYmd(focusDate))
    if (!dayName) return
    setRouteActionPending(true)
    try {
      const movePayload = {
        aspirePropertyId: Number(aspirePropertyId),
        toDaysOfWeek: [dayName],
        moveReason: 'route_builder_add'
      }
      // If we can resolve focused crew to a route, send toRouteId
      const focusRoute = availableRoutes.find(r => r.crewId === focusCrewId)
      if (focusRoute) {
        movePayload.toRouteId = focusRoute.routeId
      } else {
        movePayload.toCrewId = focusCrewId
      }
      const resp = await postMoveWithApprovalCheck(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`, movePayload)
      console.log('Move response', resp)
      const snapCount = (resp?.data?.snapshotsAffected?.pulled || 0) + (resp?.data?.snapshotsAffected?.pushed || 0)
      const snapSuffix = snapCount > 0 ? ` (${snapCount} snapshots updated)` : ''
      if (onSnackbar) onSnackbar(`Added to ${focusCrewName}'s ${dayName} route` + snapSuffix, 'success')
      await Promise.all([
        fetchMapData(),
        fetchScenarioMetrics(),
        fetchFocusContextStats(),
        fetchFocusedRoute()
      ])
    } catch (err) {
      if (err.message === 'Move cancelled by user') { setRouteActionPending(false); return }
      console.error('Error adding property to day route:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to add property', 'error')
    } finally {
      setRouteActionPending(false)
    }
  }

  // Unassign a property from its current crew (set crew to unassigned)
  const unassignProperty = async (aspirePropertyId) => {
    if (!selectedScenarioId || !aspirePropertyId) {
      if (onSnackbar) onSnackbar('Select a scenario first', 'warning')
      return
    }
    setRouteActionPending(true)
    try {
      const resp = await postMoveWithApprovalCheck(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`, {
        aspirePropertyId: Number(aspirePropertyId),
        toCrewId: 'unassigned',
        toDaysOfWeek: [],
        moveReason: 'route_builder_unassign'
      })
      const snapCount = (resp?.data?.snapshotsAffected?.pulled || 0) + (resp?.data?.snapshotsAffected?.pushed || 0)
      const snapSuffix = snapCount > 0 ? ` (${snapCount} snapshots updated)` : ''
      if (onSnackbar) onSnackbar('Property unassigned' + snapSuffix, 'success')
      setSelectedPropertyId(null)
      await Promise.all([
        fetchMapData(),
        fetchScenarioMetrics(),
        fetchFocusContextStats(),
        fetchFocusedRoute()
      ])
    } catch (err) {
      if (err.message === 'Move cancelled by user') { setRouteActionPending(false); return }
      console.error('Error unassigning property:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to unassign', 'error')
    } finally {
      setRouteActionPending(false)
    }
  }

  // Capacity Matrix: fetch for the heatmap whenever scenario or week changes
  const fetchCapacityMatrix = useCallback(async () => {
    if (!selectedScenarioId) return
    setCapacityLoading(true)
    try {
      const resp = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/best-fit`, {
        weekOf: focusDate || toYmd(new Date())
      })
      setCapacityMatrix(resp.data?.capacityMatrix || null)
    } catch (err) {
      console.error('Capacity matrix error:', err)
      setCapacityMatrix(null)
    } finally {
      setCapacityLoading(false)
    }
  }, [url, selectedScenarioId, focusDate])

  useEffect(() => {
    if (selectedScenarioId) {
      fetchCapacityMatrix()
    } else {
      setCapacityMatrix(null)
    }
  }, [selectedScenarioId, focusDate, fetchCapacityMatrix])

  // Fleet Routes: fetch route geometry for all crews in fleet mode
  const fetchFleetRoutes = useCallback(async () => {
    if (!isFleetMode || !focusDate || !showFleetRoutes) {
      setFleetRoutes([])
      return
    }
    const crewList = availableRoutes.length
      ? availableRoutes.map(r => ({ crewId: r.crewId, routeId: r.routeId })).filter(c => c.crewId)
      : availableCrews.map(c => ({ crewId: c.crewId, routeId: null }))
    if (!crewList.length) { setFleetRoutes([]); return }

    setFleetRoutesLoading(true)
    try {
      const results = await Promise.allSettled(
        crewList.map(c => {
          const routeUrl = selectedScenarioId
            ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${c.crewId}/route`
            : `${url}/api/hmi/territory/crews/${c.crewId}/route`
          return axios.get(routeUrl, { params: { date: focusDate } })
            .then(resp => ({
              crewId: c.crewId,
              routeId: c.routeId,
              path: (resp.data?.route?.geometry?.coordinates || [])
                .map(coord => {
                  const [lng, lat] = coord || []
                  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
                  return { lat, lng }
                })
                .filter(Boolean)
            }))
        })
      )
      setFleetRoutes(
        results
          .filter(r => r.status === 'fulfilled' && r.value.path.length > 1)
          .map(r => r.value)
      )
    } catch (err) {
      console.error('Fleet routes fetch error:', err)
      setFleetRoutes([])
    } finally {
      setFleetRoutesLoading(false)
    }
  }, [selectedScenarioId, isFleetMode, focusDate, showFleetRoutes, availableRoutes, availableCrews, url])

  useEffect(() => {
    fetchFleetRoutes()
  }, [fetchFleetRoutes])

  // Best Fit: fetch suggestions when unassigned property selected
  const fetchBestFit = useCallback(async (propId) => {
    if (!selectedScenarioId || !propId) return
    setBestFitLoading(true)
    try {
      const resp = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/best-fit`, {
        aspirePropertyId: Number(propId),
        weekOf: focusDate || toYmd(new Date()),
        limit: 10
      })
      setBestFitData(resp.data)
      if (resp.data?.capacityMatrix) setCapacityMatrix(resp.data.capacityMatrix)
    } catch (err) {
      console.error('Best fit error:', err)
      setBestFitData(null)
    } finally {
      setBestFitLoading(false)
    }
  }, [url, selectedScenarioId, focusDate])

  useEffect(() => {
    if (!selectedPropertyId) {
      setBestFitData(null)
      return
    }
    // Only auto-fetch for unassigned properties
    const feature = allPropertyFeatures.find(f => Number(f.properties?.aspirePropertyId) === Number(selectedPropertyId))
    if (!feature) { setBestFitData(null); return }
    const props = feature.properties || {}
    const dateOvr = effectiveCrewByProperty.get(Number(props.aspirePropertyId))
    const effRouteId = dateOvr?.routeId || props.assignedRouteId
    const effCrewId = dateOvr?.crewId || props.assignedCrewId
    if (effRouteId || effCrewId) {
      setBestFitData(null)
      return
    }
    fetchBestFit(selectedPropertyId)
  }, [selectedPropertyId, allPropertyFeatures, effectiveCrewByProperty, fetchBestFit])

  // Best Fit: handle assign action
  const bestFitAssign = async (suggestion) => {
    if (!selectedScenarioId || !suggestion) return
    const aspirePropertyId = Number(selectedPropertyFeature?.properties?.aspirePropertyId)
    if (!aspirePropertyId) return
    setRouteActionPending(true)
    try {
      const payload = {
        aspirePropertyId,
        moveReason: 'best_fit_assign',
        toRouteId: suggestion.routeId,
        toDaysOfWeek: [suggestion.dayOfWeek]
      }
      await postMoveWithApprovalCheck(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/move`, payload)
      if (onSnackbar) onSnackbar(`Assigned to ${suggestion.routeName} — ${suggestion.dayOfWeek}`, 'success')
      setBestFitData(null)
      await Promise.all([
        fetchMapData(),
        fetchScenarioMetrics(),
        fetchFocusContextStats(),
        fetchFocusedRoute()
      ])
    } catch (err) {
      if (err.message === 'Move cancelled by user') { setRouteActionPending(false); return }
      console.error('Best fit assign error:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to assign', 'error')
    } finally {
      setRouteActionPending(false)
    }
  }

  // Check if the selected property is on the focused route
  // Also verify the property is still assigned to the focused crew (avoids stale "On route"
  // chip after a reassign/unassign when focusedRoute hasn't refreshed yet)
  const selectedIsOnRoute = useMemo(() => {
    if (!selectedProps?.aspirePropertyId || !focusedRoute?.stops?.length || !focusCrewId) return false
    const inStops = focusedRoute.stops.some(s => Number(s.aspirePropertyId) === Number(selectedProps.aspirePropertyId))
    if (!inStops) return false
    // Confirm effective assignment still points at the focused crew
    const effCrewId = selectedEffectiveCrewId ? String(selectedEffectiveCrewId) : ''
    const effRouteId = selectedEffectiveRouteId ? String(selectedEffectiveRouteId) : ''
    if (!effCrewId && !effRouteId) return false // unassigned
    // Match by crewId or by routeId belonging to the focused crew
    if (effCrewId === focusCrewId) return true
    const focusRoute = availableRoutes.find(r => r.crewId === focusCrewId)
    if (focusRoute && effRouteId === focusRoute.routeId) return true
    return false
  }, [selectedProps, focusedRoute, focusCrewId, selectedEffectiveCrewId, selectedEffectiveRouteId, availableRoutes])

  const selectedFrequencyCode = String(selectedProps?.frequencyCode || '').trim().toUpperCase()
  const selectedFrequencyLabel = selectedProps?.frequencyLabel || getFrequencyLabelFromCode(selectedFrequencyCode)
  const selectedWeeklyAnchorEligible = selectedProps
    ? (typeof selectedProps.weeklyAnchorEligible === 'boolean'
      ? selectedProps.weeklyAnchorEligible
      : selectedFrequencyCode === 'W')
    : false

  const focusedRoutePath = useMemo(() => {
    const coords = focusedRoute?.route?.geometry?.coordinates || []
    return coords
      .map(item => {
        const [lng, lat] = item || []
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
        return { lat, lng }
      })
      .filter(Boolean)
  }, [focusedRoute])

  // Fit bounds when crew, date, or fleet mode changes — includes route paths and yard
  const prevViewRef = useRef('')
  useEffect(() => {
    const viewKey = `${focusCrewId}|${focusRouteId}|${focusDate}`
    if (!mapRef || !window.google?.maps) return
    if (viewKey === prevViewRef.current) return
    prevViewRef.current = viewKey

    // Small delay so route data has time to arrive after crew/date change
    const timer = setTimeout(() => {
      const bounds = new window.google.maps.LatLngBounds()
      propertyFeatures.forEach(f => {
        const [lng, lat] = f.geometry.coordinates
        if (Number.isFinite(lat) && Number.isFinite(lng)) bounds.extend({ lat, lng })
      })
      // Include focused crew route path
      if (focusedRoutePath.length > 1 && !isFleetMode) {
        focusedRoutePath.forEach(pt => bounds.extend(pt))
      }
      // Include fleet route paths
      if (isFleetMode && showFleetRoutes) {
        fleetRoutes.forEach(fr => fr.path.forEach(pt => bounds.extend(pt)))
      }
      if (!bounds.isEmpty()) {
        mapRef.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [mapRef, focusCrewId, focusRouteId, focusDate, propertyFeatures, focusedRoutePath, isFleetMode, showFleetRoutes, fleetRoutes])

  // Phase 4B: Remaining capacity (how many minutes left before 8h OT threshold)
  const remainingCapacityMin = useMemo(() => {
    if (!focusContextStats?.summary) return DAILY_OT_THRESHOLD_MIN
    const svcMin = Math.round((focusContextStats.summary.serviceMin || 0))
    const driveMin = focusContextStats.summary.driveMin || 0
    return Math.max(0, DAILY_OT_THRESHOLD_MIN - svcMin - driveMin)
  }, [focusContextStats])

  // Phase 4A: Route builder candidates
  const routeBuilderCandidates = useMemo(() => {
    if (!focusCrewId || !focusDate || !focusedRoute?.stops?.length) return []

    const routeStops = focusedRoute.stops || []
    const onRouteIds = new Set(routeStops.map(s => Number(s.aspirePropertyId)).filter(Number.isFinite))

    return allPropertyFeatures
      .filter(f => !onRouteIds.has(Number(f.properties?.aspirePropertyId)))
      .map(f => {
        const p = f.properties || {}
        const [lng, lat] = f.geometry?.coordinates || []
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

        // Distance to nearest route stop
        let minDist = Infinity
        routeStops.forEach(stop => {
          if (Number.isFinite(stop.latitude) && Number.isFinite(stop.longitude)) {
            const d = haversineMiles(lat, lng, stop.latitude, stop.longitude)
            if (d < minDist) minDist = d
          }
        })

        const svcMinutes = getServiceMinutes(p)
        const estimatedMin = Number(p.estimatedMinutesOnSite || 0) || Math.round((p.estimatedHours || 0) * 60)
        const actualMin = p.medianOnSiteHours ? Math.round(p.medianOnSiteHours * 60) : null
        const delta = actualMin != null ? actualMin - estimatedMin : null
        const dueToday = filteredContextDates.length ? filteredContextDates.some(date => isDueOnDate(p, date)) : false

        // Look up assigned crew's day stats for capacity indicator (use date-specific if available)
        const dateOvr = effectiveCrewByProperty.get(Number(p.aspirePropertyId))
        const assignedRid = String(dateOvr?.routeId || p.assignedRouteId || '')
        const assignedCid = String(dateOvr?.crewId || p.assignedCrewId || '')
        const crewDayStats = capacityByRoute.get(assignedRid || null) || capacityByRoute.get(assignedCid || null) || null
        const effName = dateOvr
          ? (nameById.get(assignedRid) || nameById.get(assignedCid) || p.assignedRouteName || p.assignedCrewName || 'Unassigned')
          : (p.assignedRouteName || p.assignedCrewName || 'Unassigned')

        return {
          aspirePropertyId: Number(p.aspirePropertyId),
          propertyName: p.propertyName,
          address: p.address,
          assignedName: effName,
          frequencyCode: p.frequencyCode,
          serviceMinutes: svcMinutes,
          delta,
          distanceMiles: minDist === Infinity ? null : Math.round(minDist * 100) / 100,
          dueToday,
          crewDayStats,
          feature: f
        }
      })
      .filter(c => c && c.distanceMiles != null)
      .sort((a, b) => a.distanceMiles - b.distanceMiles)
      .slice(0, 30)
  }, [allPropertyFeatures, capacityByRoute, effectiveCrewByProperty, filteredContextDates, focusCrewId, focusDate, focusedRoute, getServiceMinutes, nameById])

  const selectedCurrentDays = selectedProps ? normalizeDaysForRow(selectedProps) : []
  const selectedCurrentWeekType = selectedProps ? rowWeekType(selectedProps) || null : null
  const selectedNextDays = scheduleDay ? [scheduleDay] : selectedCurrentDays
  const selectedNextWeekType = scheduleWeekType === 'ANY'
    ? null
    : (scheduleWeekType || selectedCurrentWeekType || null)
  const selectedPreviewRow = useMemo(() => {
    if (!selectedProps) return null
    return {
      ...selectedProps,
      daysOfWeek: selectedNextDays,
      preferredDay: scheduleDay || selectedProps.preferredDay || null,
      preferredDays: scheduleDay ? [scheduleDay] : (selectedProps.preferredDays || selectedNextDays),
      weekType: selectedNextWeekType
    }
  }, [scheduleDay, selectedNextDays, selectedNextWeekType, selectedProps])
  const hasCrewUpdate = !!selectedProps && String(selectedAssignedId || '') !== String(assignmentRouteId || '')
  const hasDayUpdate = !!selectedProps && !!scheduleDay && !areSameStringArrays(selectedCurrentDays, selectedNextDays)
  const hasWeekTypeUpdate = !!selectedProps && (
    scheduleWeekType === 'ANY'
      ? Boolean(selectedCurrentWeekType)
      : Boolean(scheduleWeekType && scheduleWeekType !== selectedCurrentWeekType)
  )
  const hasAnyMoveChange = hasCrewUpdate || hasDayUpdate || hasWeekTypeUpdate
  const pendingChanges = useMemo(() => {
    const changes = []
    if (hasCrewUpdate) {
      const fromName = assignmentOptions.find(o => o.id === selectedAssignedId)?.name || 'Unassigned'
      const toName = assignmentOptions.find(o => o.id === assignmentRouteId)?.name || 'Unassigned'
      changes.push(`Route: ${fromName} -> ${toName}`)
    }
    if (hasDayUpdate) {
      changes.push(`Day: ${(selectedCurrentDays.join(', ') || 'none')} -> ${(selectedNextDays.join(', ') || 'none')}`)
    }
    if (hasWeekTypeUpdate) {
      changes.push(`Week type: ${selectedCurrentWeekType || 'Any'} -> ${selectedNextWeekType || 'Any'}`)
    }
    return changes
  }, [
    assignmentOptions,
    assignmentRouteId,
    hasCrewUpdate,
    hasDayUpdate,
    hasWeekTypeUpdate,
    selectedAssignedId,
    selectedCurrentDays,
    selectedCurrentWeekType,
    selectedNextDays,
    selectedNextWeekType
  ])

  const weekDatesForBaseline = useMemo(() => {
    const weekStart = getMonday(parseYmd(focusDate))
    return WORK_DAYS.map((_, idx) => addDays(weekStart, idx))
  }, [focusDate])

  const currentOccurrencesInContext = useMemo(
    () => (selectedProps ? countDueOccurrences(selectedProps, filteredContextDates) : 0),
    [filteredContextDates, selectedProps]
  )
  const projectedOccurrencesInContext = useMemo(
    () => (selectedPreviewRow ? countDueOccurrences(selectedPreviewRow, filteredContextDates) : 0),
    [filteredContextDates, selectedPreviewRow]
  )
  const currentOccurrencesInWeek = useMemo(
    () => (selectedProps ? countDueOccurrences(selectedProps, weekDatesForBaseline) : 0),
    [selectedProps, weekDatesForBaseline]
  )
  const projectedOccurrencesInWeek = useMemo(
    () => (selectedPreviewRow ? countDueOccurrences(selectedPreviewRow, weekDatesForBaseline) : 0),
    [selectedPreviewRow, weekDatesForBaseline]
  )

  // When 'actual' mode, recompute service from route stops' medianServiceMinutes
  const displayContextStats = useMemo(() => {
    if (!focusContextStats?.summary) return null
    const base = focusContextStats.summary
    if (hoursMode !== 'actual' || !focusedRoute?.stops?.length) return base

    // Sum median service minutes from route stops (already wall-clock)
    let actualServiceMin = 0
    focusedRoute.stops.forEach(stop => {
      if (stop.medianServiceMinutes > 0) {
        actualServiceMin += stop.medianServiceMinutes
      } else {
        // Fallback to estimated (already wall-clock minutes, pre-divided by crew size on server)
        actualServiceMin += Math.round(stop.serviceMinutes || 0)
      }
    })
    const driveMin = base.driveMin || 0
    const totalMin = actualServiceMin + driveMin
    const otMin = Math.max(0, totalMin - DAILY_OT_THRESHOLD_MIN)
    return { ...base, serviceMin: actualServiceMin, totalMin, otMin }
  }, [focusContextStats, focusedRoute, hoursMode])

  const weeklyDisplayStats = useMemo(() => {
    if (!weeklyCrewStats) return null
    const isActual = hoursMode === 'actual'

    // Override the focused day's values with displayContextStats so the chip
    // always matches the daily stats shown directly above in the Route Context card.
    let weekTotalMin = 0
    const days = weeklyCrewStats.days.map(d => {
      const isFocusedDay = d.date === focusDate
      let displayTotal, displayOt
      if (isFocusedDay && displayContextStats) {
        displayTotal = displayContextStats.totalMin || 0
        displayOt = displayContextStats.otMin || 0
      } else {
        displayTotal = isActual ? d.actualTotalMin : d.totalMin
        displayOt = isActual ? d.actualOtMin : d.otMin
      }
      weekTotalMin += displayTotal
      return { ...d, displayTotal, displayOt }
    })
    const weekOtMin = Math.max(0, weekTotalMin - WEEKLY_OT_THRESHOLD_MIN)
    return { weekTotalMin, weekOtMin, days }
  }, [weeklyCrewStats, hoursMode, focusDate, displayContextStats])

  const contextSummary = displayContextStats || null
  const contextThresholdMinutes = Math.max(DAILY_OT_THRESHOLD_MIN, DAILY_OT_THRESHOLD_MIN * (filteredContextDates.length || 1))
  const currentServiceInContext = selectedServiceMinutes * currentOccurrencesInContext
  const projectedServiceInContext = selectedServiceMinutes * projectedOccurrencesInContext
  const weeklyDeltaForCrew = (crewId, targetCrewId = assignmentRouteId) => {
    if (!selectedProps || !crewId) return 0
    const currentContribution = String(selectedAssignedId || '') === String(crewId)
      ? selectedServiceMinutes * currentOccurrencesInWeek
      : 0
    const projectedContribution = String(targetCrewId || '') === String(crewId)
      ? selectedServiceMinutes * projectedOccurrencesInWeek
      : 0
    return projectedContribution - currentContribution
  }
  const focusCrewServiceDelta = (() => {
    if (!selectedProps || !focusCrewId) return 0
    const currentContribution = String(selectedAssignedId || '') === String(focusCrewId || '')
      ? selectedServiceMinutes * currentOccurrencesInContext
      : 0
    const projectedContribution = String(assignmentRouteId || '') === String(focusCrewId || '')
      ? selectedServiceMinutes * projectedOccurrencesInContext
      : 0
    return projectedContribution - currentContribution
  })()
  const projectedFocusTotalMinutes = contextSummary
    ? Math.max(0, Number(contextSummary.totalMin || 0) + focusCrewServiceDelta)
    : null
  const projectedFocusOtMinutes = projectedFocusTotalMinutes != null
    ? Math.max(0, projectedFocusTotalMinutes - contextThresholdMinutes)
    : null

  const fromCrewMetrics = selectedAssignedId ? crewMetricsById.get(selectedAssignedId) || null : null
  const toCrewMetrics = assignmentRouteId ? crewMetricsById.get(String(assignmentRouteId)) || null : null

  const fromCrewProjectedMinutes = fromCrewMetrics
    ? Math.max(0, Number(fromCrewMetrics.weeklyTotalMinutes || 0) + weeklyDeltaForCrew(selectedAssignedId, assignmentRouteId))
    : null
  const toCrewProjectedMinutes = toCrewMetrics
    ? Math.max(0, Number(toCrewMetrics.weeklyTotalMinutes || 0) + weeklyDeltaForCrew(assignmentRouteId, assignmentRouteId))
    : null

  // Build crewId→routeName lookup for display
  const routeNameByCrewId = useMemo(() => {
    const map = new Map()
    availableRoutes.forEach(r => { if (r.crewId) map.set(r.crewId, r.routeName) })
    return map
  }, [availableRoutes])

  const candidateCrews = useMemo(() => {
    if (!selectedScenarioId) return []
    if (!selectedPropertyFeature) return []
    const [selectedLng, selectedLat] = selectedPropertyFeature.geometry.coordinates || []
    if (!Number.isFinite(selectedLat) || !Number.isFinite(selectedLng)) return []

    return availableCrews
      .map(crew => {
        const centroid = centroidByCrewId.get(String(crew.crewId))
        if (!centroid) return null

        const distanceMiles = haversineMiles(selectedLat, selectedLng, centroid.lat, centroid.lng)
        const metrics = crewMetricsById.get(String(crew.crewId)) || null
        const baseTotal = Number(metrics?.weeklyTotalMinutes || 0)
        const projectedTotal = Math.max(0, baseTotal + weeklyDeltaForCrew(String(crew.crewId), String(crew.crewId)))
        const projectedOt = Math.max(0, projectedTotal - WEEKLY_OT_THRESHOLD_MIN)
        const projectedDelta = projectedTotal - baseTotal

        return {
          crewId: String(crew.crewId),
          crewName: crew.crewName,
          routeName: routeNameByCrewId.get(String(crew.crewId)) || crew.crewName,
          distanceMiles,
          baseTotal,
          projectedTotal,
          projectedOt,
          projectedDelta
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.distanceMiles - b.distanceMiles)
      .slice(0, 6)
  }, [
    availableCrews,
    centroidByCrewId,
    crewMetricsById,
    currentOccurrencesInWeek,
    projectedOccurrencesInWeek,
    routeNameByCrewId,
    selectedAssignedId,
    selectedPropertyFeature,
    selectedScenarioId,
    selectedServiceMinutes
  ])

  if (!process.env.REACT_APP_GMAPKEY) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <Typography color="textSecondary">Google Maps API key not configured (REACT_APP_GMAPKEY)</Typography>
      </Box>
    )
  }

  console.log('contextSummary..', contextSummary)
  console.log('projectedFocusTotalMinutes...', projectedFocusTotalMinutes)

  return (
    <div>
      {/* Unified Toolbar */}
      <div className={classes.toolbar}>
        <Select
          value={isRouteMode ? focusRouteId : focusCrewId}
          onChange={(e) => {
            const next = String(e.target.value || '')
            if (next === '__ALL__') {
              setFocusRouteId('__ALL__')
              setFocusCrewId('')
              return
            }
            if (isRouteMode) {
              setFocusRouteId(next)
              const route = availableRoutes.find(r => r.routeId === next)
              setFocusCrewId(route?.crewId || '')
            } else {
              setFocusCrewId(next)
            }
          }}
          variant="outlined"
          size="small"
          className={classes.selectControl}
          displayEmpty
        >
          <MenuItem value="">Focus route</MenuItem>
          <MenuItem value="__ALL__"><strong>All Routes (Fleet View)</strong></MenuItem>
          {isRouteMode
            ? availableRoutes.map(route => (
              <MenuItem key={route.routeId} value={route.routeId}>{route.routeName}</MenuItem>
            ))
            : availableCrews.map(crew => (
              <MenuItem key={crew.crewId} value={crew.crewId}>{crew.crewName}</MenuItem>
            ))
          }
        </Select>
        {isFleetMode && <Chip size="small" label="FLEET VIEW" style={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 700 }} />}
        {isFleetMode && (
          <Chip
            size="small"
            label={fleetTodayOnly ? "Today's Stops" : 'All Properties'}
            variant={fleetTodayOnly ? 'default' : 'outlined'}
            clickable
            onClick={() => setFleetTodayOnly(prev => !prev)}
            style={fleetTodayOnly
              ? { backgroundColor: '#1976d2', color: '#fff', fontWeight: 600 }
              : { fontWeight: 600 }
            }
          />
        )}
        {isFleetMode && (
          <Chip
            size="small"
            label={fleetRoutesLoading ? 'Loading...' : (showFleetRoutes ? 'Routes ON' : 'Routes')}
            variant={showFleetRoutes ? 'default' : 'outlined'}
            clickable
            onClick={() => setShowFleetRoutes(prev => !prev)}
            disabled={!selectedScenarioId}
            style={showFleetRoutes
              ? { backgroundColor: '#00BCD4', color: '#fff', fontWeight: 600 }
              : { fontWeight: 600 }
            }
          />
        )}

        {!isFleetMode && focusCrewId && (
          <Chip
            size="small"
            label={showRouteStops ? 'Route Stops' : 'All Crews'}
            variant={showRouteStops ? 'default' : 'outlined'}
            clickable
            onClick={() => setShowRouteStops(prev => !prev)}
            style={showRouteStops
              ? { backgroundColor: '#1976d2', color: '#fff', fontWeight: 600 }
              : { fontWeight: 600 }
            }
          />
        )}

        <Chip
          size="small"
          label="Unassigned"
          variant={showUnassigned ? 'default' : 'outlined'}
          clickable
          onClick={() => setShowUnassigned(prev => !prev)}
          style={showUnassigned
            ? { backgroundColor: '#757575', color: '#fff', fontWeight: 600 }
            : { color: '#757575', fontWeight: 600 }
          }
        />

        <Chip
          size="small"
          label="Irregular Mtc"
          variant={showIrregularMtc ? 'default' : 'outlined'}
          clickable
          onClick={() => setShowIrregularMtc(prev => !prev)}
          style={showIrregularMtc
            ? { backgroundColor: '#ef6c00', color: '#fff', fontWeight: 600 }
            : { color: '#ef6c00', borderColor: '#ef6c00', fontWeight: 600 }
          }
        />

        <IconButton size="small" onClick={() => {
          const cur = parseYmd(focusDate)
          const day = cur.getDay()
          const offset = day === 1 ? -3 : day === 0 ? -2 : -1
          setFocusDate(toYmd(addDays(cur, offset)))
        }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <TextField
          type="date"
          value={focusDate}
          onChange={(e) => setFocusDate(String(e.target.value || toYmd(new Date())))}
          variant="outlined"
          size="small"
        />
        <IconButton size="small" onClick={() => {
          const cur = parseYmd(focusDate)
          const day = cur.getDay()
          const offset = day === 5 ? 3 : day === 6 ? 2 : 1
          setFocusDate(toYmd(addDays(cur, offset)))
        }}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <Chip
          size="small"
          label={`Wk ${getWeekTypeForDate(parseYmd(focusDate))}`}
          style={{
            fontWeight: 600,
            fontSize: '0.75rem',
            backgroundColor: getWeekTypeForDate(parseYmd(focusDate)) === 'A' ? '#e3f2fd' : '#fce4ec',
            color: getWeekTypeForDate(parseYmd(focusDate)) === 'A' ? '#1565c0' : '#c62828'
          }}
        />

        <Select
          value={planningMode}
          onChange={(e) => setPlanningMode(String(e.target.value || 'day'))}
          variant="outlined"
          size="small"
          className={classes.selectCompact}
        >
          <MenuItem value="day">Day</MenuItem>
          <MenuItem value="week">Week</MenuItem>
          <MenuItem value="two-week">2-Week</MenuItem>
        </Select>

        <div className={classes.toolbarDivider} />

        <Tooltip title="Filters">
          <IconButton size="small" onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
            <Badge badgeContent={activeSecondaryFilterCount} color="secondary" overlap="rectangular">
              <FilterListIcon />
            </Badge>
          </IconButton>
        </Tooltip>

        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <div className={classes.filterPopover}>
            <FormControlLabel
              control={<Switch checked={showVipOnly} onChange={(e) => setShowVipOnly(e.target.checked)} size="small" />}
              label="VIP only"
            />
            <FormControlLabel
              control={<Switch checked={showAnchorsOnly} onChange={(e) => setShowAnchorsOnly(e.target.checked)} size="small" />}
              label="Anchors only"
            />
            <FormControlLabel
              control={<Switch checked={showLockedOnly} onChange={(e) => setShowLockedOnly(e.target.checked)} size="small" />}
              label="Locked only"
            />
            <FormControlLabel
              control={<Switch checked={showCentroids} onChange={(e) => setShowCentroids(e.target.checked)} size="small" />}
              label="Crew centroids"
            />
            <FormControlLabel
              control={<Switch checked={showDueOnly} onChange={(e) => setShowDueOnly(e.target.checked)} size="small" />}
              label="Due in context"
            />
            <Divider />
            <Select
              value={contextWeekType}
              onChange={(e) => setContextWeekType(String(e.target.value || 'ALL'))}
              variant="outlined"
              size="small"
              fullWidth
            >
              <MenuItem value="ALL">Any Week</MenuItem>
              <MenuItem value="A">Week A</MenuItem>
              <MenuItem value="B">Week B</MenuItem>
            </Select>
            <Select
              value={frequencyFocus}
              onChange={(e) => setFrequencyFocus(String(e.target.value || 'all'))}
              variant="outlined"
              size="small"
              fullWidth
            >
              <MenuItem value="all">All Freq</MenuItem>
              <MenuItem value="weekly">Weekly+</MenuItem>
              <MenuItem value="biweekly">Bi-Weekly</MenuItem>
            </Select>
            <Button
              size="small"
              onClick={() => {
                setShowVipOnly(false)
                setShowAnchorsOnly(false)
                setShowLockedOnly(false)
                setShowCentroids(true)
                setShowDueOnly(false)
                setShowRouteStops(true)
                setShowUnassigned(false)
                setShowIrregularMtc(false)
                setContextWeekType('ALL')
                setFrequencyFocus('all')
              }}
              disabled={activeSecondaryFilterCount === 0}
            >
              Reset Filters
            </Button>
          </div>
        </Popover>

        <div className={classes.toolbarDivider} />

        <Tooltip title="Refresh map data">
          <IconButton size="small" onClick={fetchMapData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        {(loading || loadingMetrics || loadingFocusContextStats || loadingFocusedRoute || fleetRoutesLoading) && (
          <CircularProgress size={18} />
        )}

        <Chip size="small" variant="outlined" label={`${propertyFeatures.length} props`} />
        <Chip size="small" variant="outlined" label={`${planningMode} | ${filteredContextDates.length}d`} />

        {routeLegend.length > 0 && (
          <Tooltip title={legendOpen ? 'Hide route legend' : 'Show route legend'}>
            <Button
              size="small"
              variant="text"
              onClick={() => setLegendOpen(prev => !prev)}
              endIcon={legendOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              Legend
            </Button>
          </Tooltip>
        )}

        {!!selectedScenarioId && reviewCombos.length > 0 && (
          <Tooltip title={reviewNavOpen ? 'Close review navigator' : 'Step through each crew/day/week combo to approve'}>
            <Button
              size="small"
              variant={reviewNavOpen ? 'contained' : 'outlined'}
              onClick={() => {
                setReviewNavOpen(prev => !prev)
                if (!reviewNavOpen && filteredReviewCombos.length) {
                  navigateToCombo(filteredReviewCombos[reviewComboIdx])
                }
              }}
              style={reviewNavOpen ? { backgroundColor: '#1565c0', color: '#fff', fontWeight: 600 } : { fontWeight: 600 }}
              startIcon={<CheckCircleIcon fontSize="small" />}
            >
              Review ({approvedRouteDays.length}/{reviewCombos.length})
            </Button>
          </Tooltip>
        )}
      </div>

      {/* Review Navigator Bar */}
      <Collapse in={reviewNavOpen && filteredReviewCombos.length > 0}>
        <div className={classes.reviewNavBar}>
          <Tooltip title="Previous combo">
            <IconButton size="small" onClick={reviewNavPrev} disabled={filteredReviewCombos.length <= 1}>
              <SkipPreviousIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {(() => {
            const combo = filteredReviewCombos[reviewComboIdx]
            if (!combo) return null
            const wtColor = combo.weekType === 'A' ? '#1565c0' : '#c62828'
            const wtBg = combo.weekType === 'A' ? '#e3f2fd' : '#fce4ec'
            return (
              <>
                {combo.approved
                  ? <CheckCircleIcon style={{ color: '#4CAF50', fontSize: 18 }} />
                  : <RadioButtonUncheckedIcon style={{ color: '#bbb', fontSize: 18 }} />
                }
                <Typography className={classes.reviewNavLabel}>
                  {combo.routeName || combo.crewId}
                </Typography>
                <Chip size="small" label={combo.dayName.slice(0, 3)} style={{ fontWeight: 600, fontSize: '0.72rem' }} />
                <Chip size="small" label={`Wk ${combo.weekType}`} style={{ fontWeight: 600, fontSize: '0.72rem', backgroundColor: wtBg, color: wtColor }} />
              </>
            )
          })()}

          <Tooltip title="Next combo">
            <IconButton size="small" onClick={reviewNavNext} disabled={filteredReviewCombos.length <= 1}>
              <SkipNextIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <div className={classes.toolbarDivider} />

          <Typography className={classes.reviewNavProgress}>
            {reviewComboIdx + 1} / {filteredReviewCombos.length}
          </Typography>

          <Chip
            size="small"
            label={skipApproved ? 'Unapproved only' : 'Show all'}
            variant={skipApproved ? 'default' : 'outlined'}
            clickable
            onClick={() => {
              setSkipApproved(prev => !prev)
              setReviewComboIdx(0)
            }}
            style={skipApproved ? { backgroundColor: '#4CAF50', color: '#fff', fontWeight: 600, fontSize: '0.72rem' } : { fontWeight: 600, fontSize: '0.72rem' }}
          />

          <Typography className={classes.reviewNavProgress} style={{ color: '#4CAF50' }}>
            {approvedRouteDays.length} approved
          </Typography>
        </div>
      </Collapse>

      {/* Collapsible Route Legend */}
      <Collapse in={legendOpen}>
        <div className={classes.legendGrid}>
          {routeLegend.map(([id, name]) => (
            <Chip
              key={id}
              size="small"
              variant={String(routeFilterId) === String(id) ? 'default' : 'outlined'}
              label={name}
              icon={<span className={classes.legendDot} style={{ backgroundColor: getRouteColor(id, routeIds) || getCrewColor(id, crewIds) }} />}
              onClick={() => setRouteFilterId(String(routeFilterId) === String(id) ? '' : String(id))}
              clickable
            />
          ))}
          <Chip
            size="small"
            variant={routeFilterId === 'unassigned' ? 'default' : 'outlined'}
            label="Unassigned"
            icon={<span className={classes.legendDot} style={{ backgroundColor: '#9E9E9E' }} />}
            onClick={() => setRouteFilterId(routeFilterId === 'unassigned' ? '' : 'unassigned')}
            clickable
          />
        </div>
      </Collapse>

      <div className={classes.layout}>
        {/* Map */}
        <div className={classes.mapPane}>
          <LoadScript googleMapsApiKey={process.env.REACT_APP_GMAPKEY}>
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              onLoad={onMapLoad}
              options={{
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: true
              }}
            >
              {propertyFeatures.map(feature => {
                const [lng, lat] = feature.geometry.coordinates
                const props = feature.properties
                return (
                  <Marker
                    key={props.aspirePropertyId}
                    position={{ lat, lng }}
                    icon={getMarkerIcon(feature)}
                    title={props.propertyName}
                    onClick={() => handlePropertySelect(feature)}
                  />
                )
              })}

              {/* Suggestion pins */}
              {showSuggestionPins && (candidatesDueOnly ? routeBuilderCandidates.filter(c => c.dueToday) : routeBuilderCandidates)
                .map((candidate, idx) => {
                  const [lng, lat] = candidate.feature?.geometry?.coordinates || []
                  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
                  const color = SUGGESTION_PIN_COLORS[idx % SUGGESTION_PIN_COLORS.length]
                  const label = String(idx + 1)
                  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">` +
                    `<path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.27 21.73 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>` +
                    `<text x="14" y="18" text-anchor="middle" fill="#fff" font-size="12" font-weight="700" font-family="Arial,sans-serif">${label}</text>` +
                    `</svg>`
                  return (
                    <Marker
                      key={`suggestion-${candidate.aspirePropertyId}`}
                      position={{ lat, lng }}
                      icon={{
                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                        scaledSize: { width: 28, height: 40 },
                        anchor: { x: 14, y: 40 }
                      }}
                      title={`#${idx + 1} ${candidate.propertyName || ''}`}
                      zIndex={1000 + idx}
                      onClick={() => handlePropertySelect(candidate.feature)}
                    />
                  )
                })}

              {focusedRoutePath.length > 1 && !isFleetMode && (
                <Polyline
                  path={focusedRoutePath}
                  options={{
                    strokeColor: '#00BCD4',
                    strokeOpacity: 0.9,
                    strokeWeight: 4
                  }}
                />
              )}

              {/* Fleet mode: polylines for all crews */}
              {isFleetMode && showFleetRoutes && fleetRoutes.map(fr => (
                <Polyline
                  key={`fleet-route-${fr.crewId}`}
                  path={fr.path}
                  options={{
                    strokeColor: fr.routeId
                      ? (getRouteColor(fr.routeId, routeIds) || getCrewColor(fr.crewId, crewIds))
                      : getCrewColor(fr.crewId, crewIds),
                    strokeOpacity: 0.8,
                    strokeWeight: 3
                  }}
                />
              ))}

              {centroidFeatures.map(feature => {
                const [lng, lat] = feature.geometry.coordinates
                const props = feature.properties
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
                const centroidId = props.routeId || props.crewId
                const centroidName = props.routeName || props.crewName
                return (
                  <Marker
                    key={`centroid-${centroidId}`}
                    position={{ lat, lng }}
                    icon={{
                      path: window.google?.maps?.SymbolPath?.BACKWARD_CLOSED_ARROW,
                      scale: 7,
                      fillColor: getRouteColor(centroidId, routeIds) || getCrewColor(centroidId, crewIds),
                      fillOpacity: 1,
                      strokeColor: '#000',
                      strokeWeight: 2
                    }}
                    title={`${centroidName} (centroid)`}
                    onClick={() => setRouteFilterId(String(centroidId))}
                  />
                )
              })}

              {/* HMI Yard marker */}
              <Marker
                position={{ lat: HMI_YARD.lat, lng: HMI_YARD.lng }}
                icon={{
                  path: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
                  fillColor: '#FF6F00',
                  fillOpacity: 1,
                  strokeColor: '#fff',
                  strokeWeight: 2,
                  scale: 1.4,
                  anchor: window.google?.maps ? new window.google.maps.Point(12, 22) : undefined
                }}
                title={HMI_YARD.label}
                zIndex={999}
              />
            </GoogleMap>
          </LoadScript>
        </div>

        {/* Sidebar — 4 cards */}
        <Paper className={classes.sidePanel} elevation={0}>

          {/* Card 1 — Property Info (non-collapsible) */}
          <div className={classes.sidebarCard} style={{ overflow: 'visible' }}>
            <div style={{ padding: '8px 10px' }}>
              {!selectedProps ? (
                <Typography className={classes.emptyHint}>
                  Click a property pin to open details.
                </Typography>
              ) : (
                <>
                  <div className={classes.propertyInfoRow}>
                    <Typography className={classes.propertyName}>
                      {selectedProps.propertyName || '-'}
                    </Typography>
                    <Chip
                      size="small"
                      label={selectedEffectiveName}
                      style={{
                        backgroundColor: selectedAssignedId
                          ? (getRouteColor(selectedAssignedId, routeIds) || getCrewColor(selectedAssignedId, crewIds))
                          : '#9E9E9E',
                        color: '#fff'
                      }}
                    />
                  </div>
                  <Typography className={classes.propertyMeta}>
                    {selectedProps.address || '-'}{selectedProps.city ? `, ${selectedProps.city}` : ''} | Aspire #{selectedProps.aspirePropertyId}
                  </Typography>
                  <div className={classes.badgeRow}>
                    <Chip size="small" variant="outlined" label={`Freq: ${selectedProps.frequencyCode || '?'}`} />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Est: ${formatMinutes(Number(selectedProps.estimatedMinutesOnSite || 0) || Math.round((selectedProps.estimatedHours || 0) * 60))}`}
                    />
                    {(selectedProps.medianOnSiteMinutes > 0 || selectedProps.medianOnSiteHours > 0) && (
                      <Chip size="small" variant="outlined" label={`Act: ${formatMinutes(selectedProps.medianOnSiteMinutes || Math.round((selectedProps.medianOnSiteHours || 0) * 60))}`} />
                    )}
                    {selectedProps.isVip && <Chip size="small" color="secondary" label="VIP" />}
                    {selectedProps.isLocked && <Chip size="small" label="Locked" />}
                    {selectedProps.isAnchor && <Chip size="small" label="Anchor" />}
                  </div>
                  {selectedProps.daysOfWeek?.length > 0 && (
                    <Typography className={classes.propertyMeta} style={{ marginTop: 2 }}>
                      Days: {selectedProps.daysOfWeek.join(', ')}
                      {selectedProps.weekType ? ` (${selectedProps.weekType})` : ''}
                    </Typography>
                  )}
                  {selectedProps.scheduledVisitDates?.length > 0 && (
                    <Typography className={classes.propertyMeta} style={{ marginTop: 2 }}>
                      Upcoming visits: {selectedProps.scheduledVisitDates
                        .filter(v => v.date >= new Date().toISOString().slice(0, 10))
                        .slice(0, 6)
                        .map(v => {
                          const d = new Date(v.date + 'T00:00:00')
                          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        })
                        .join(', ') || 'None upcoming'}
                    </Typography>
                  )}

                  {/* Property action buttons */}
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {!!selectedAssignedId && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={routeActionPending ? <CircularProgress size={14} color="inherit" /> : <LinkOffIcon fontSize="small" />}
                        onClick={() => unassignProperty(selectedProps.aspirePropertyId)}
                        disabled={!selectedScenarioId || routeActionPending}
                        style={{ color: '#f44336', borderColor: '#f44336' }}
                      >
                        Unassign
                      </Button>
                    )}
                    {!!focusCrewId && !!focusDate && !selectedIsOnRoute && !!selectedAssignedId && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={routeActionPending ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon fontSize="small" />}
                        onClick={() => addToDay(selectedProps.aspirePropertyId)}
                        disabled={!selectedScenarioId || routeActionPending}
                      >
                        Move to {focusCrewName}
                      </Button>
                    )}
                    {!!focusCrewId && !!focusDate && !selectedIsOnRoute && !selectedAssignedId && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={routeActionPending ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon fontSize="small" />}
                        onClick={() => addToDay(selectedProps.aspirePropertyId)}
                        disabled={!selectedScenarioId || routeActionPending}
                      >
                        Assign to {focusCrewName}
                      </Button>
                    )}
                    {selectedIsOnRoute && (
                      <Chip size="small" label="On route" style={{ backgroundColor: '#00BCD4', color: '#fff' }} />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Card 1.5 — Best Fit Suggestions (unassigned properties only) */}
          {!!bestFitData && !selectedAssignedId && !!selectedProps && (
            <div className={classes.sidebarCard}>
              <div className={classes.sidebarCardHeader} onClick={() => setBestFitOpen(prev => !prev)}>
                <Typography className={classes.sidebarCardHeaderTitle}>
                  Best Fit{bestFitData.suggestions?.length ? ` (top ${Math.min(bestFitData.suggestions.length, 5)})` : ''}
                </Typography>
                {bestFitOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </div>
              <Collapse in={bestFitOpen}>
                <div className={classes.sidebarCardContent}>
                  {bestFitLoading && (
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <CircularProgress size={16} />
                      <Typography className={classes.emptyHint}>Analyzing routes...</Typography>
                    </Box>
                  )}
                  {!bestFitLoading && (!bestFitData.suggestions || bestFitData.suggestions.length === 0) && (
                    <Typography className={classes.emptyHint}>No suggestions available.</Typography>
                  )}
                  {!bestFitLoading && bestFitData.suggestions?.length > 0 && (
                    <div className={classes.bestFitList}>
                      {bestFitData.suggestions.slice(0, 5).map(s => {
                        const capPct = Math.round(s.projectedMetrics?.capacityPct || 0)
                        const barColor = capPct >= 100 ? '#f44336' : capPct >= 85 ? '#ff9800' : capPct >= 60 ? '#FF9800' : '#4CAF50'
                        return (
                          <div key={`${s.routeId}-${s.dayOfWeek}`} className={classes.bestFitItem}>
                            <span className={classes.bestFitRank}>{s.rank}</span>
                            <div className={classes.bestFitInfo}>
                              <Typography className={classes.bestFitName}>
                                {s.routeName} — {s.dayOfWeek}
                              </Typography>
                              <Typography className={classes.bestFitMeta}>
                                {s.distanceMiles != null ? `${s.distanceMiles} mi` : '?'} · {formatMinutes(s.dayMetrics?.totalMin || 0)} used / 8h · {s.dayMetrics?.stops || 0} stops
                              </Typography>
                              <div className={classes.capacityBar}>
                                <div
                                  className={classes.capacityBarFill}
                                  style={{ width: `${Math.min(100, capPct)}%`, backgroundColor: barColor }}
                                />
                              </div>
                              <Typography className={classes.bestFitMeta} style={{ marginTop: 1 }}>
                                Projected: {formatMinutes(s.projectedMetrics?.totalMin || 0)} ({capPct}%)
                                {s.projectedMetrics?.otMin > 0 && <span style={{ color: '#f44336', fontWeight: 600 }}> +{formatMinutes(s.projectedMetrics.otMin)} OT</span>}
                              </Typography>
                            </div>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              style={{ flexShrink: 0, fontSize: '0.72rem', padding: '2px 8px', minWidth: 0 }}
                              onClick={() => bestFitAssign(s)}
                              disabled={!selectedScenarioId || routeActionPending}
                            >
                              {routeActionPending ? <CircularProgress size={14} color="inherit" /> : 'ASSIGN'}
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </Collapse>
            </div>
          )}
          {bestFitLoading && !bestFitData && !!selectedProps && !selectedAssignedId && (
            <div className={classes.sidebarCard}>
              <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <CircularProgress size={16} />
                <Typography className={classes.emptyHint}>Finding best fit...</Typography>
              </div>
            </div>
          )}

          {/* Route Capacity Heatmap — always visible when scenario is selected */}
          {capacityMatrix?.routes?.length > 0 && (
            <div className={classes.sidebarCard}>
              <div className={classes.sidebarCardHeader} onClick={() => setHeatmapOpen(prev => !prev)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                  <Typography className={classes.sidebarCardHeaderTitle}>
                    Route Capacity (week of {capacityMatrix.weekOf || '?'})
                  </Typography>
                  {approvedRouteDays.length > 0 && (
                    <span style={{ fontSize: 11, color: '#4CAF50', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {approvedRouteDays.length} / {(capacityMatrix?.routes?.length || 0) * 10} approved
                    </span>
                  )}
                </div>
                {capacityLoading && <CircularProgress size={14} style={{ marginRight: 4 }} />}
                {heatmapOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </div>
              <Collapse in={heatmapOpen}>
                <div className={classes.sidebarCardContent} style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'min(35vh, 280px)' }}>
                  <table className={classes.heatmapTable}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Route</th>
                        <th>Mon</th>
                        <th>Tue</th>
                        <th>Wed</th>
                        <th>Thu</th>
                        <th>Fri</th>
                      </tr>
                    </thead>
                    <tbody>
                      {capacityMatrix.routes.map(route => {
                        const days = route.days || {}
                        const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
                        return (
                          <tr key={route.routeId}>
                            <td className={classes.heatmapRouteName}>
                              <Tooltip title={`${route.routeName}${route.distanceMiles != null ? ` (${route.distanceMiles} mi)` : ''}`} arrow>
                                <span>{route.routeName?.replace(/\s*\(.*\)/, '') || route.routeId}</span>
                              </Tooltip>
                            </td>
                            {dayOrder.map((dayName, dayIdx) => {
                              const dm = days[dayName] || {}
                              const pct = dm.capacityPct || 0
                              const dotColor = pct > 85 ? '#f44336' : pct > 60 ? '#FF9800' : '#4CAF50'
                              const cellWeekType = capacityMatrix.weekOf
                                ? getWeekTypeForDate(addDays(new Date(capacityMatrix.weekOf + 'T00:00:00'), dayIdx))
                                : ''
                              const isApproved = approvedSet.has(`${route.crewId}_${dayName}_${cellWeekType}`)
                              return (
                                <td
                                  key={dayName}
                                  onClick={() => {
                                    if (route.crewId) setFocusCrewId(route.crewId)
                                    if (route.routeId) setFocusRouteId(route.routeId)
                                    if (dayIdx >= 0 && capacityMatrix.weekOf) {
                                      const weekStart = new Date(capacityMatrix.weekOf + 'T00:00:00')
                                      const targetDate = new Date(weekStart)
                                      targetDate.setDate(weekStart.getDate() + dayIdx)
                                      setFocusDate(toYmd(targetDate))
                                    }
                                  }}
                                >
                                  <Tooltip title={`${dm.stops || 0} stops · ${formatMinutes(dm.totalMin || 0)} / 8h (${Math.round(pct)}%)${isApproved ? ` ✓ Approved (Wk ${cellWeekType})` : ''}`} arrow>
                                    <span
                                      className={classes.heatmapDot}
                                      style={{
                                        backgroundColor: dotColor,
                                        ...(isApproved && { border: '2px solid #4CAF50', boxShadow: '0 0 0 1px #fff' })
                                      }}
                                    />
                                  </Tooltip>
                                </td>
                              )
                            })}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </Collapse>
            </div>
          )}

          {/* Card 2 — Fleet Day Summary OR Route Context */}
          {isFleetMode ? (
            <div className={classes.sidebarCard}>
              <div className={classes.sidebarCardHeader} onClick={() => setRouteContextOpen(prev => !prev)}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography className={classes.sidebarCardHeaderTitle}>
                    Fleet Day Summary — {fleetDayStats?.dayName || getDayName(parseYmd(focusDate)) || '?'}
                  </Typography>
                  <div className={classes.toggleChips} onClick={(e) => e.stopPropagation()}>
                    <span
                      className={hoursMode === 'estimated' ? classes.toggleChipActive : classes.toggleChip}
                      onClick={() => setHoursMode('estimated')}
                    >
                      Estimated
                    </span>
                    <span
                      className={hoursMode === 'actual' ? classes.toggleChipActive : classes.toggleChip}
                      onClick={() => setHoursMode('actual')}
                    >
                      Actual
                    </span>
                  </div>
                </div>
                {routeContextOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </div>
              <Collapse in={routeContextOpen}>
                <div className={classes.sidebarCardContent}>
                  {!capacityMatrix && (
                    <Typography className={classes.emptyHint}>No capacity data — select a scenario to see fleet stats.</Typography>
                  )}
                  {!!fleetDayStats && (
                    <>
                      <div className={classes.compactStatsGrid}>
                        <div>
                          <div className={classes.statLabel}>Crews</div>
                          <div className={classes.statValue}>{fleetDayStats.crewCount}</div>
                        </div>
                        <div>
                          <div className={classes.statLabel}>Total Stops</div>
                          <div className={classes.statValue}>{fleetDayStats.totalStops}</div>
                        </div>
                        <div>
                          <div className={classes.statLabel}>Service</div>
                          <div className={classes.statValue}>{formatMinutes(fleetDayStats.totalServiceMin)}</div>
                        </div>
                        <div>
                          <div className={classes.statLabel}>Drive</div>
                          <div className={classes.statValue}>{formatMinutes(fleetDayStats.totalDriveMin)}</div>
                        </div>
                        <div>
                          <div className={classes.statLabel}>Total</div>
                          <div className={classes.statValue}>{formatMinutes(fleetDayStats.totalTotalMin)}</div>
                        </div>
                        <div>
                          <div className={classes.statLabel}>OT</div>
                          <div className={classes.statValue}>
                            {formatMinutes(fleetDayStats.totalOtMin)}
                            {fleetDayStats.crewsInOt > 0 && (
                              <span style={{ color: '#f44336', fontSize: '0.7rem' }}> ({fleetDayStats.crewsInOt} crews)</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Per-route breakdown */}
                      <Box mt={1}>
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 4 }}>
                          Per-Route Breakdown ({fleetDayStats.crewDays.length})
                        </Typography>
                        <div style={{ maxHeight: 'min(40vh, 320px)', overflowY: 'auto', overflowX: 'hidden' }}>
                          {fleetDayStats.crewDays.map(cd => {
                            const pct = Math.round((cd.capacityPct || ((cd.totalMin || 0) / DAILY_OT_THRESHOLD_MIN * 100)) || 0)
                            const barColor = pct >= 100 ? '#f44336' : pct >= 85 ? '#ff9800' : '#4caf50'
                            return (
                              <div
                                key={cd.routeId}
                                style={{ padding: '4px 0', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                                onClick={() => {
                                  if (cd.routeId) setFocusRouteId(cd.routeId)
                                  if (cd.crewId) setFocusCrewId(cd.crewId)
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography style={{ fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 170 }}>
                                    {cd.routeName || cd.routeId}
                                  </Typography>
                                  <Typography style={{ fontSize: '0.72rem', color: '#666', flexShrink: 0, marginLeft: 4 }}>
                                    {cd.stops || 0} stops · {formatMinutes(cd.totalMin || 0)}
                                  </Typography>
                                </div>
                                <div className={classes.capacityBar} style={{ marginTop: 2 }}>
                                  <div
                                    className={classes.capacityBarFill}
                                    style={{ width: `${Math.min(100, pct)}%`, backgroundColor: barColor }}
                                  />
                                </div>
                                <Typography style={{ fontSize: '0.66rem', color: '#888', marginTop: 1 }}>
                                  {pct}% capacity
                                  {(cd.otMin || 0) > 0 && <span style={{ color: '#f44336', fontWeight: 600 }}> +{formatMinutes(cd.otMin)} OT</span>}
                                </Typography>
                              </div>
                            )
                          })}
                        </div>
                      </Box>
                    </>
                  )}
                  {!!capacityMatrix && !fleetDayStats && (
                    <Typography className={classes.emptyHint}>No data for this day (weekend?).</Typography>
                  )}
                </div>
              </Collapse>
            </div>
          ) : (
          <div className={classes.sidebarCard}>
            <div className={classes.sidebarCardHeader} onClick={() => setRouteContextOpen(prev => !prev)}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography className={classes.sidebarCardHeaderTitle}>Route Context</Typography>
                <div className={classes.toggleChips} onClick={(e) => e.stopPropagation()}>
                  <span
                    className={hoursMode === 'estimated' ? classes.toggleChipActive : classes.toggleChip}
                    onClick={() => setHoursMode('estimated')}
                  >
                    Estimated
                  </span>
                  <span
                    className={hoursMode === 'actual' ? classes.toggleChipActive : classes.toggleChip}
                    onClick={() => setHoursMode('actual')}
                  >
                    Actual
                  </span>
                </div>
              </div>
              {routeContextOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </div>
            <Collapse in={routeContextOpen}>
              <div className={classes.sidebarCardContent}>
                {!focusCrewId && (
                  <Typography className={classes.emptyHint}>Pick a focus route to see stats.</Typography>
                )}
                {!!focusCrewId && !focusContextStats && !loadingFocusContextStats && (
                  <Typography className={classes.emptyHint}>No stats for this context.</Typography>
                )}

                {!!contextSummary && (
                  <>
                    <Typography className={classes.detailText} style={{ marginBottom: 6 }}>
                      {focusCrewName} | {planningMode}{hoursMode === 'actual' ? ' (actual)' : ''}
                    </Typography>
                    <div className={classes.compactStatsGrid}>
                      <div>
                        <div className={classes.statLabel}>Stops</div>
                        <div className={classes.statValue}>{contextSummary.stops || 0}</div>
                      </div>
                      <div>
                        <div className={classes.statLabel}>Service</div>
                        <div className={classes.statValue}>{formatMinutes(contextSummary.serviceMin || 0)}</div>
                      </div>
                      <div>
                        <div className={classes.statLabel}>Drive</div>
                        <div className={classes.statValue}>{formatMinutes(contextSummary.driveMin || 0)}</div>
                      </div>
                      <div>
                        <div className={classes.statLabel}>Total</div>
                        <div className={classes.statValue}>{formatMinutes(contextSummary.totalMin || 0)}</div>
                      </div>
                      <div>
                        <div className={classes.statLabel}>OT</div>
                        <div className={classes.statValue}>{formatMinutes(contextSummary.otMin || 0)}</div>
                      </div>
                      <div>
                        <div className={classes.statLabel}>Threshold</div>
                        <div className={classes.statValue}>{formatMinutes(contextThresholdMinutes)}</div>
                      </div>
                    </div>

                    {!!weeklyDisplayStats && (
                      <div className={classes.weekSummaryBox}>
                        <div className={classes.weekSummaryHeader}>
                          <span>Week (Mon–Fri)</span>
                          <span>{formatMinutes(weeklyDisplayStats.weekTotalMin)}</span>
                        </div>
                        <div className={classes.capacityBar}>
                          <div
                            className={classes.capacityBarFill}
                            style={{
                              width: `${Math.min(100, (weeklyDisplayStats.weekTotalMin / WEEKLY_OT_THRESHOLD_MIN) * 100)}%`,
                              backgroundColor: weeklyDisplayStats.weekTotalMin >= WEEKLY_OT_THRESHOLD_MIN
                                ? '#f44336'
                                : weeklyDisplayStats.weekTotalMin >= WEEKLY_OT_THRESHOLD_MIN * 0.85
                                  ? '#ff9800'
                                  : '#4caf50'
                            }}
                          />
                        </div>
                        <div className={classes.weekSummaryFooter}>
                          <span style={weeklyDisplayStats.weekOtMin > 0 ? { color: '#f44336', fontWeight: 600 } : undefined}>
                            OT: {formatMinutes(weeklyDisplayStats.weekOtMin)}
                          </span>
                          <span>Limit: 40h</span>
                        </div>
                        <div className={classes.weekDayRow}>
                          {weeklyDisplayStats.days.map(d => {
                            const isFocused = d.date === focusDate
                            const isDailyOt = d.displayOt > 0
                            return (
                              <span
                                key={d.date}
                                className={[
                                  classes.weekDayChip,
                                  isFocused ? classes.weekDayChipActive : '',
                                  isDailyOt ? classes.weekDayChipOt : ''
                                ].filter(Boolean).join(' ')}
                              >
                                {d.dayName.charAt(0)} {formatMinutes(d.displayTotal)}
                              </span>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {!!selectedProps && !!focusCrewId && !!contextSummary && (
                      <div className={classes.impactBox}>
                        <div className={classes.impactRow}>
                          <span>Context total</span>
                          <span className={classes.impactValue}>
                            {formatMinutes(contextSummary.totalMin || 0)} {'>'} {formatMinutes(projectedFocusTotalMinutes || 0)}
                          </span>
                        </div>
                        <div className={classes.impactRow}>
                          <span>Delta</span>
                          <span className={classes.impactValue}>{formatDeltaMinutes(focusCrewServiceDelta)}</span>
                        </div>
                        <div className={classes.impactRow}>
                          <span>Projected OT</span>
                          <span className={classes.impactValue}>{formatMinutes(projectedFocusOtMinutes || 0)}</span>
                        </div>
                        {projectedFocusOtMinutes > 0 && (
                          <Typography className={classes.warningText} variant="caption">
                            Exceeds threshold by {formatMinutes(projectedFocusOtMinutes)}
                          </Typography>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Route Stops list (collapsible) */}
                {focusedRoute?.stops?.length > 0 && (
                  <Box mt={1}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      style={{ cursor: 'pointer', marginBottom: 4 }}
                      onClick={() => setRouteStopsOpen(prev => !prev)}
                    >
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 600 }}>
                        Route Stops ({focusedRoute.stops.length})
                        {focusedRoute.meta?.missingCoordinates > 0 && (
                          <span className={classes.routeStopMissing}>
                            {' '}({focusedRoute.meta.missingCoordinates} missing coords)
                          </span>
                        )}
                      </Typography>
                      {routeStopsOpen ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
                    </Box>
                    <Collapse in={routeStopsOpen}>
                      <div style={{ maxHeight: 'min(35vh, 280px)', overflowY: 'auto', overflowX: 'hidden' }}>
                        {focusedRoute.stops.map(stop => {
                          const hasCoords = Number.isFinite(stop.latitude) && Number.isFinite(stop.longitude) &&
                            stop.latitude !== 0 && stop.longitude !== 0
                          const svcMin = stop.serviceMinutes || 0
                          const medianMin = stop.medianServiceMinutes
                          return (
                            <div
                              key={stop.aspirePropertyId}
                              className={classes.routeStopItem}
                              onClick={() => {
                                if (hasCoords && mapRef) {
                                  mapRef.panTo({ lat: stop.latitude, lng: stop.longitude })
                                  mapRef.setZoom(15)
                                }
                                setSelectedPropertyId(stop.aspirePropertyId)
                              }}
                            >
                              <span className={classes.routeStopOrder}>{stop.order}</span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Typography className={classes.routeStopName} noWrap>
                                  {stop.propertyName || `Aspire #${stop.aspirePropertyId}`}
                                </Typography>
                                <Typography className={classes.routeStopDetail}>
                                  Est: {formatMinutes(svcMin)}
                                  {medianMin != null && ` · Act: ${formatMinutes(medianMin)}`}
                                  {stop.driveMiles != null && ` · ${stop.driveMiles} mi`}
                                </Typography>
                                {stop.address && (
                                  <Typography className={classes.routeStopDetail} noWrap>{stop.address}</Typography>
                                )}
                                {!hasCoords && (
                                  <Typography className={classes.routeStopMissing}>No coordinates — no marker</Typography>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </Collapse>
                  </Box>
                )}

                <Box mt={0.75} display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      fetchFocusContextStats()
                      fetchFocusedRoute()
                    }}
                    disabled={!selectedScenarioId || !focusCrewId}
                  >
                    Refresh Focus
                  </Button>
                  {!!selectedScenarioId && !!focusCrewId && !!focusDate && (() => {
                    const dayName = getDayName(parseYmd(focusDate))
                    if (!dayName || !['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].includes(dayName)) return null
                    const focusWeekType = getWeekTypeForDate(parseYmd(focusDate))
                    const isCurrentApproved = approvedSet.has(`${focusCrewId}_${dayName}_${focusWeekType}`)
                    const weekLabel = `Wk ${focusWeekType}`
                    return (
                      <Button
                        variant={isCurrentApproved ? 'contained' : 'outlined'}
                        size="small"
                        style={isCurrentApproved ? { backgroundColor: '#4CAF50', color: '#fff' } : { borderColor: '#4CAF50', color: '#4CAF50' }}
                        onClick={async () => {
                          try {
                            const resp = await axios.post(
                              `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/approve-route-day`,
                              { crewId: focusCrewId, routeId: focusRouteId || null, dayOfWeek: dayName, weekType: focusWeekType, unapprove: isCurrentApproved }
                            )
                            setApprovedRouteDays(resp.data?.approvedRouteDays || [])
                            if (onSnackbar) {
                              onSnackbar(
                                isCurrentApproved
                                  ? `Unapproved ${focusCrewName} ${dayName} (${weekLabel})`
                                  : `Approved ${focusCrewName} ${dayName} (${weekLabel})`,
                                'success'
                              )
                            }
                          } catch (err) {
                            if (onSnackbar) onSnackbar(err.response?.data?.message || 'Failed to update approval', 'error')
                          }
                        }}
                      >
                        {isCurrentApproved ? `\u21A9 Unapprove (${weekLabel})` : `\u2713 Approve Day (${weekLabel})`}
                      </Button>
                    )
                  })()}
                </Box>
              </div>
            </Collapse>
          </div>
          )}

          {/* Card 4 — Route Builder (collapsible, default open) */}
          {!!focusCrewId && !!focusDate && !!focusedRoute?.stops?.length && (
            <div className={classes.sidebarCard}>
              <div className={classes.sidebarCardHeader} onClick={() => setRouteBuilderOpen(prev => !prev)}>
                <Typography className={classes.sidebarCardHeaderTitle}>
                  Route Builder ({routeBuilderCandidates.length})
                </Typography>
                {routeBuilderOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </div>
              <Collapse in={routeBuilderOpen}>
                <div className={classes.sidebarCardContent}>
                  {/* Capacity bar */}
                  {contextSummary && (() => {
                    const used = contextSummary.totalMin || 0
                    const pct = Math.min(100, Math.round((used / DAILY_OT_THRESHOLD_MIN) * 100))
                    const barColor = pct >= 100 ? '#f44336' : pct >= 85 ? '#ff9800' : '#4caf50'
                    return (
                      <Box mb={1}>
                        <Typography className={classes.candidateMeta}>
                          Capacity: {formatMinutes(used)} / {formatMinutes(DAILY_OT_THRESHOLD_MIN)} ({pct}%)
                        </Typography>
                        <div style={{ height: 8, borderRadius: 4, backgroundColor: '#e0e0e0', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, backgroundColor: barColor, borderRadius: 4, transition: 'width 0.3s' }} />
                        </div>
                      </Box>
                    )
                  })()}

                  {/* Due today toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={candidatesDueOnly}
                        onChange={(e) => setCandidatesDueOnly(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography style={{ fontSize: '0.78rem' }}>Due today only</Typography>}
                    style={{ marginBottom: 4 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showSuggestionPins}
                        onChange={(e) => setShowSuggestionPins(e.target.checked)}
                        size="small"
                        color="secondary"
                      />
                    }
                    label={<Typography style={{ fontSize: '0.78rem' }}>Show on map</Typography>}
                    style={{ marginBottom: 4, marginLeft: 8 }}
                  />

                  {/* Candidate list */}
                  <div className={classes.routeBuilderList}>
                    {(candidatesDueOnly ? routeBuilderCandidates.filter(c => c.dueToday) : routeBuilderCandidates)
                      .map((candidate, idx) => {
                        const exceeds = candidate.serviceMinutes > remainingCapacityMin
                        const pinColor = SUGGESTION_PIN_COLORS[idx % SUGGESTION_PIN_COLORS.length]
                        return (
                          <div
                            key={candidate.aspirePropertyId}
                            className={classes.candidateItem}
                            style={{
                              ...(exceeds ? { opacity: 0.5 } : {}),
                              ...(showSuggestionPins ? { borderLeft: `4px solid ${pinColor}` } : {})
                            }}
                          >
                            <div className={classes.candidateHeader}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {showSuggestionPins && (
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                                    backgroundColor: pinColor, color: '#fff',
                                    fontSize: 10, fontWeight: 700, lineHeight: 1
                                  }}>
                                    {idx + 1}
                                  </span>
                                )}
                                <Typography className={classes.candidateName}>
                                  {candidate.propertyName || `#${candidate.aspirePropertyId}`}
                                </Typography>
                                <Typography className={classes.candidateMeta}>
                                  {candidate.address || '-'}
                                </Typography>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <Chip size="small" label={`${candidate.distanceMiles} mi`} />
                                {candidate.crewDayStats?.capacityPct > 95 && (
                                  <Chip size="small" label="relief" style={{ marginLeft: 2, backgroundColor: '#FF9800', color: '#fff', fontWeight: 600 }} />
                                )}
                                {candidate.dueToday && (
                                  <Chip size="small" color="primary" label="Due" style={{ marginLeft: 2 }} />
                                )}
                              </div>
                            </div>
                            <Typography className={classes.candidateMeta}>
                              {formatMinutes(candidate.serviceMinutes)}
                              {candidate.delta != null && candidate.delta !== 0 && (
                                <span className={candidate.delta > 0 ? classes.deltaPositive : classes.deltaNegative}>
                                  {' '}({candidate.delta > 0 ? '+' : ''}{candidate.delta}m)
                                </span>
                              )}
                              {candidate.frequencyCode && (
                                <span style={{ marginLeft: 4, padding: '0 3px', borderRadius: 2, backgroundColor: '#e8e8e8', fontSize: '0.65rem', fontWeight: 600, color: '#555' }}>
                                  {candidate.frequencyCode}
                                </span>
                              )}
                              {' | '}{candidate.assignedName}
                              {candidate.crewDayStats && (() => {
                                const cs = candidate.crewDayStats
                                const pct = cs.capacityPct || 0
                                const barColor = pct > 90 ? '#f44336' : pct >= 70 ? '#FF9800' : '#4CAF50'
                                const barWidth = Math.min(pct, 120)
                                const tooltipText = `${cs.routeName || 'Crew'}: ${cs.stops || 0} stops · ${formatMinutes(cs.totalMin || 0)} / ${formatMinutes(DAILY_OT_THRESHOLD_MIN)} (${Math.round(pct)}%)${candidate.serviceMinutes ? ` — removing frees ~${formatMinutes(candidate.serviceMinutes)}` : ''}`
                                return (
                                  <Tooltip title={tooltipText} placement="top" arrow>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, cursor: 'default' }}>
                                      <span style={{ display: 'inline-block', width: 32, height: 6, backgroundColor: '#e0e0e0', borderRadius: 3, overflow: 'hidden', verticalAlign: 'middle' }}>
                                        <span style={{ display: 'block', width: `${Math.min(barWidth / 1.2, 100)}%`, height: '100%', backgroundColor: barColor, borderRadius: 3 }} />
                                      </span>
                                      <span style={{ marginLeft: 3, fontSize: 11, fontWeight: 600, color: barColor }}>
                                        {pct > 100 ? `OT +${Math.round(cs.otMin || 0)}m` : `${Math.round(pct)}%`}
                                      </span>
                                    </span>
                                  </Tooltip>
                                )
                              })()}
                            </Typography>
                            <div className={classes.candidateActions}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PlaceIcon fontSize="small" />}
                                onClick={() => handlePropertySelect(candidate.feature)}
                              >
                                Focus
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={routeActionPending ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon fontSize="small" />}
                                onClick={() => addToDay(candidate.aspirePropertyId)}
                                disabled={!selectedScenarioId || routeActionPending}
                              >
                                Add to Day
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    {routeBuilderCandidates.length === 0 && (
                      <Typography className={classes.emptyHint}>No candidates found.</Typography>
                    )}
                  </div>
                </div>
              </Collapse>
            </div>
          )}

          {/* Card 5 — Advanced (temporarily hidden) */}
          {showAdvancedSidebar && (
            <div className={classes.sidebarCard}>
              <div className={classes.sidebarCardHeader} onClick={() => setAdvancedOpen(prev => !prev)}>
                <Typography className={classes.sidebarCardHeaderTitle}>Advanced</Typography>
                {advancedOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </div>
              <Collapse in={advancedOpen}>
                <div className={classes.sidebarCardContent}>

                {/* Anchor actions */}
                <Typography variant="subtitle2" className={classes.panelTitle}>Anchor Actions</Typography>
                {selectedProps && !selectedWeeklyAnchorEligible && (
                  <Typography className={classes.warningText} variant="caption">
                    Weekly only: this property is {selectedFrequencyLabel}
                  </Typography>
                )}
                <div className={classes.actionRow}>
                  <Select
                    value={anchorCrewId}
                    onChange={(e) => setAnchorCrewId(String(e.target.value || ''))}
                    variant="outlined"
                    size="small"
                    className={classes.selectControl}
                    disabled={!selectedProps || applyingAnchor}
                  >
                    <MenuItem value="">Anchor crew</MenuItem>
                    {availableCrews.map(crew => (
                      <MenuItem key={crew.crewId} value={crew.crewId}>{crew.crewName}</MenuItem>
                    ))}
                  </Select>
                </div>
                <div className={classes.actionRow}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={applyingAnchor ? <CircularProgress size={14} color="inherit" /> : <StarsIcon />}
                    onClick={() => assignAsAnchor(true)}
                    disabled={!selectedProps || applyingAnchor || !anchorCrewId || !selectedWeeklyAnchorEligible}
                  >
                    {applyingAnchor ? 'Saving...' : 'Set Anchor'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => assignAsAnchor(false)}
                    disabled={!selectedProps || applyingAnchor || !selectedProps?.isAnchor}
                  >
                    Remove Anchor
                  </Button>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {/* Decision support details */}
                {selectedProps && (
                  <>
                    <Typography variant="subtitle2" className={classes.panelTitle}>Decision Support</Typography>
                    <Typography className={classes.detailText}>
                      Service time: <strong>{formatMinutes(selectedServiceMinutes)}</strong>
                    </Typography>
                    <Typography className={classes.detailText}>
                      Occurrences: <strong>{currentOccurrencesInContext}</strong> {'>'} <strong>{projectedOccurrencesInContext}</strong>
                    </Typography>
                    <Typography className={classes.detailText}>
                      Service in context: <strong>{formatMinutes(currentServiceInContext)}</strong> {'>'} <strong>{formatMinutes(projectedServiceInContext)}</strong>
                    </Typography>

                    {!!fromCrewMetrics && (
                      <Box mt={0.6}>
                        <Typography className={classes.detailText}>
                          From: <strong>{selectedEffectiveName}</strong> | {formatMinutes(fromCrewMetrics.weeklyTotalMinutes)}
                          {fromCrewProjectedMinutes != null && hasAnyMoveChange ? ` > ${formatMinutes(fromCrewProjectedMinutes)}` : ''}
                        </Typography>
                      </Box>
                    )}

                    {!!toCrewMetrics && (
                      <Box mt={0.4}>
                        <Typography className={classes.detailText}>
                          To: <strong>{assignmentOptions.find(o => o.id === assignmentRouteId)?.name || '-'}</strong> | {formatMinutes(toCrewMetrics.weeklyTotalMinutes)}
                          {toCrewProjectedMinutes != null && hasAnyMoveChange ? ` > ${formatMinutes(toCrewProjectedMinutes)}` : ''}
                        </Typography>
                        {toCrewProjectedMinutes != null && hasAnyMoveChange && toCrewProjectedMinutes > WEEKLY_OT_THRESHOLD_MIN && (
                          <Typography className={classes.warningText} variant="caption">
                            OT risk: {formatMinutes(toCrewProjectedMinutes - WEEKLY_OT_THRESHOLD_MIN)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </>
                )}

                {/* Candidate routes/crews list */}
                {!!candidateCrews.length && (
                  <Box mt={1}>
                    <Divider style={{ marginBottom: 8 }} />
                    <Typography variant="subtitle2" className={classes.panelTitle}>Nearest Routes</Typography>
                    <div className={classes.nearList}>
                      {candidateCrews.map(item => (
                        <div key={item.crewId} className={classes.nearItem}>
                          <div className={classes.nearHeader}>
                            <Typography className={classes.nearName}>{item.routeName}</Typography>
                            <Chip size="small" label={`${item.distanceMiles.toFixed(1)} mi`} />
                          </div>
                          <Typography className={classes.nearSub}>
                            {formatMinutes(item.baseTotal)} {'>'} {formatMinutes(item.projectedTotal)} ({formatDeltaMinutes(item.projectedDelta)})
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setAssignmentRouteId(item.crewId)}
                            style={{ marginTop: 4 }}
                          >
                            Use
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Box>
                )}

                {/* Nearby properties */}
                {!!selectedProps && (
                  <Box mt={1}>
                    <Divider style={{ marginBottom: 8 }} />
                    <Typography variant="subtitle2" className={classes.panelTitle}>Nearby Properties</Typography>
                    {nearbyProperties.length === 0 ? (
                      <Typography className={classes.emptyHint}>None available.</Typography>
                    ) : (
                      <div className={classes.nearList}>
                        {nearbyProperties.slice(0, 5).map(item => {
                          const np = item.feature.properties || {}
                          return (
                            <div key={np.aspirePropertyId} className={classes.nearItem}>
                              <div className={classes.nearHeader}>
                                <Typography className={classes.nearName}>{np.propertyName || `#${np.aspirePropertyId}`}</Typography>
                                <Chip size="small" label={`${item.distanceMiles.toFixed(2)} mi`} />
                              </div>
                              <Typography className={classes.nearSub}>
                                {(() => {
                                  const ovr = effectiveCrewByProperty.get(Number(np.aspirePropertyId))
                                  if (!ovr) return np.assignedRouteName || np.assignedCrewName || 'Unassigned'
                                  return nameById.get(String(ovr.routeId)) || nameById.get(String(ovr.crewId)) || np.assignedRouteName || np.assignedCrewName || 'Unassigned'
                                })()}
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PlaceIcon fontSize="small" />}
                                onClick={() => handlePropertySelect(item.feature)}
                                style={{ marginTop: 4 }}
                              >
                                Focus
                              </Button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </Box>
                )}
                </div>
              </Collapse>
            </div>
          )}

          {!!meta && (
            <Chip
              size="small"
              variant="outlined"
              label={`${meta.propertyCount || 0} props / ${meta.routeCount || meta.crewCount || 0} routes`}
            />
          )}
        </Paper>
      </div>

      {/* Confirmation dialog for approved-route / locked-property overrides */}
      <Dialog open={confirmDialog.open} onClose={() => handleConfirmDialogClose(false)} maxWidth="sm" fullWidth>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {confirmDialog.type === 'locked'
            ? <LockIcon style={{ color: '#e65100' }} />
            : <WarningIcon style={{ color: '#e65100' }} />}
          {confirmDialog.title || 'Confirm'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 12 }}>
            {confirmDialog.message}
          </Typography>

          {confirmDialog.type === 'approved' && (
            <Box style={{ background: '#fff3e0', borderRadius: 4, padding: '10px 14px', marginBottom: 12 }}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                This will affect ALL future snapshots, not just today.
              </Typography>
              <Typography variant="body2">
                {confirmDialog.details?.fromApproved && confirmDialog.details?.toApproved
                  ? 'Both the source and destination route days are approved. Moving will modify finalized routes on both sides across all matching dates.'
                  : confirmDialog.details?.fromApproved
                    ? 'The source route day is approved. Removing this property will modify a finalized route across all matching dates.'
                    : 'The destination route day is approved. Adding this property will modify a finalized route across all matching dates.'}
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
                If you only want to change a single day, cancel and edit that day's snapshot directly.
              </Typography>
            </Box>
          )}

          {confirmDialog.type === 'locked' && (
            <Box style={{ background: '#fff3e0', borderRadius: 4, padding: '10px 14px', marginBottom: 12 }}>
              <Typography variant="body2">
                This property is assigned to a specific crew. Overriding the lock will move it and update all matching future snapshots.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 16px' }}>
          <Button onClick={() => handleConfirmDialogClose(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#e65100', color: '#fff' }}
            onClick={() => handleConfirmDialogClose(true)}
          >
            Move Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
